<template>
  <div class="form-editor-container app-page app-page--fill">
    <el-card>
      <div class="main-content" ref="mainContentRef">
        <!-- 左侧列表 -->
        <div class="left-panel" ref="leftPanelRef" :style="{ width: leftPanelWidth + 'px' }">
          <div class="list-header">
            <el-button v-if="can('新增')" type="primary" @click="addNewForm" :icon="Plus">
              新增
            </el-button>
            <el-input
              v-model="quickKeyword"
              placeholder="关键词"
              clearable
              class="quick-keyword-input"
             
            />
            <el-button v-if="can('列表')" @click="handleQuickQuery" :loading="listLoading">快速查询</el-button>
            <el-button v-if="can('快速同步')" type="primary" @click="handleQuickSync" :loading="quickSyncLoading">快速同步</el-button>
            <el-button
              type="primary"
              size="small"
              class="app-refresh-btn"
              @click="loadFormList"
              :loading="listLoading"
              :icon="Refresh"
              style="margin-left: auto;"
              title="刷新"
              circle
            />
          </div>

          <div class="form-list-wrapper">
            <DynamicTable ref="dynamicTableRef" :data="formListData" :model="tableModel" :loading="listLoading"
              :show-column-selector="false" :show-export="false" :show-edit-button="false" :show-delete-button="false"
              :pagination="formPagination" :column-order="['编码', '名称', '主责部门', '项目负责人']" :column-widths="{ '名称': 260 }"
              :active-row-key="activeFormRowKey"
              @delete="handleFormDelete" @filter="handleFilter" @page-change="handleFormPageChange"
              @page-size-change="handleFormPageSizeChange">
              <template #action="{ row }">
                <el-button v-if="can('项目详情')" type="primary" size="small" :icon="View" plain @click="handleFormEdit(row)">
                  
                </el-button>
              </template>
            </DynamicTable>
          </div>
        </div>

        <!-- 拖拽分隔条 -->
        <div class="resize-handle" @mousedown="startResize"></div>

        <!-- 右侧编辑区域 -->
        <div class="right-panel" v-loading="loading" element-loading-text="加载中...">
          <div v-if="currentForm" class="edit-area">
            <!-- 编辑按钮区域 -->
            <div class="edit-mode-control">
              <el-button v-if="!isEditMode && can('编辑')" type="primary" @click="enterEditMode" :icon="Edit"
                :disabled="!canEnterEditMode">
                编辑
              </el-button>
              <el-button v-if="isEditMode" type="info" @click="exitEditMode" :icon="Close">
                取消编辑
              </el-button>
              <el-badge :value="projectFileCount" type="success" class="current-file-badge">
                <el-button type="primary" plain :icon="Document" @click="openProjectFileDialog" :loading="projectFilesLoading">
                  当前文件
                </el-button>
              </el-badge>
              <el-button type="primary" :icon="Upload" @click="openProjectFileUploadDialog">
                文件上传
              </el-button>
            </div>

            <!-- 表单内容 -->
            <el-form :model="currentForm" :rules="formRules" ref="formRef" label-width="120px" class="form-content">
              <el-form-item label="项目名称：">
                <div v-if="isEditMode" class="limited-field">
                  <el-input v-model="currentForm.name" placeholder="请输入项目名称" />
                </div>
                <span v-else class="form-text">{{ currentForm.name || '-' }}</span>
              </el-form-item>

              <el-form-item label="项目开始时间：">
                <div class="limited-field">
                  <el-date-picker v-model="currentForm.startTime" type="date" :readonly="!isEditMode"
                    :disabled="!isEditMode" placeholder="请选择项目开始时间" value-format="YYYY-MM-DD" />
                </div>
              </el-form-item>

              <el-form-item label="编码：">
                <div v-if="isEditMode" class="limited-field">
                  <el-input v-model="currentForm.code" placeholder="请输入编码" />
                </div>
                <span v-else class="form-text">{{ currentForm.code || '-' }}</span>
              </el-form-item>

              <el-form-item label="项目负责人：">
                <div v-if="isEditMode" class="member-select-row">
                  <span class="form-text">{{ currentForm.projectManager || '-' }}</span>
                  <el-button v-if="canAny('配置团队获取部门', '获取部门下成员')" type="primary" plain @click="openMemberDialog('manager')">选择</el-button>
                </div>
                <span v-else class="form-text">{{ currentForm.projectManager || '-' }}</span>
              </el-form-item>

              <!-- 主责部门 -->
              <el-form-item label="主责部门：">
                <div class="team-tags-inline">
                  <el-tag v-if="currentForm.responsibleDepartment" type="warning" effect="light" size="default"
                    class="dept-tag" :closable="isEditMode" @close="currentForm.responsibleDepartment = ''">
                    <el-icon class="dept-tag-icon">
                      <OfficeBuilding />
                    </el-icon>
                    {{ currentForm.responsibleDepartment }}
                  </el-tag>
                  <el-button v-if="isEditMode && !currentForm.responsibleDepartment" type="primary" plain @click="openDeptDialog">选择</el-button>
                </div>
              </el-form-item>

              <!-- 项目助理（值为空不显示，蓝色tag） -->
              <div v-if="isEditMode || assistantList.length > 0" class="team-display-section">
                <el-form-item label="项目助理：">
                  <div class="team-tags-inline">
                    <el-tag v-for="member in assistantList" :key="member.code" type="" size="default" class="team-tag"
                      :closable="isEditMode" @close="removeAssistantMember(member.code)">
                      {{ member.name }}
                    </el-tag>
                    <el-button v-if="isEditMode && canAny('配置团队获取部门', '获取部门下成员')" type="primary" plain @click="openMemberDialog('assistant')">
                      选择
                    </el-button>
                  </div>
                </el-form-item>
              </div>

              <!-- 执行团队（值为空不显示） -->
              <div v-if="isEditMode || executionTeamList.length > 0" class="team-display-section">
                <el-form-item label="执行团队：">
                  <div class="team-tags-inline">
                    <el-tag v-for="member in executionTeamList" :key="member.code" :closable="isEditMode"
                      @close="removeTeamMember(member.code)" type="success" size="default" class="team-tag">
                      {{ member.name }}
                    </el-tag>
                    <el-button v-if="isEditMode && canAny('配置团队获取部门', '获取部门下成员')" type="primary" plain @click="openMemberDialog('execution')">
                      选择
                    </el-button>
                  </div>
                </el-form-item>
              </div>

              <el-form-item label="说明" prop="remark">
                <el-input v-model="currentForm.remark" :readonly="!isEditMode" type="textarea" placeholder="请输入说明信息" />
              </el-form-item>

              <el-form-item label="建筑个数" prop="buildingCount" class="building-count-item">
                <div class="building-count-row">
                  <el-input-number v-model="currentForm.buildingCount" :readonly="!isEditMode" :min="0" :max="20"
                    :precision="0" controls-position="right" placeholder="请输入建筑个数，最大20" style="width: 200px" />
                  <el-button v-if="isEditMode" type="primary" @click="generateBuildings">
                    确定
                  </el-button>
                </div>
              </el-form-item>

              <div v-if="currentForm.buildings.length > 0" class="buildings-container">
                <div class="buildings-header">
                  <h3>楼栋信息</h3>
                </div>
                <div class="buildings-list">
                  <el-row :gutter="10">
                    <el-col v-for="(building, index) in currentForm.buildings" :key="building.id" :xs="12" :sm="8"
                      :md="6" :lg="4">
                      <el-input v-if="building.editing && isEditMode" v-model="building.name" placeholder="请输入楼栋名称"
                        size="small" @blur="finishBuildingRename(index)" @keyup.enter="finishBuildingRename(index)"
                        style="margin-bottom: 10px" />
                      <el-button v-else :type="building.selected ? 'primary' : 'default'" @click="selectBuilding(index)"
                        style="width: 100%; margin-bottom: 10px" @dblclick="startBuildingRename(index)">
                        {{ building.name }}
                      </el-button>
                    </el-col>
                  </el-row>
                </div>

                <div v-if="selectedBuildingIndex !== null" class="floor-setting">
                  <el-divider />
                  <h4>{{ currentForm.buildings[selectedBuildingIndex].name }} 层数设置</h4>
                  <el-row :gutter="20">
                    <el-col :span="12">
                      <el-form-item label="楼栋名称" :required="true">
                        <el-input v-model="currentForm.buildings[selectedBuildingIndex].name" :readonly="!isEditMode"
                          placeholder="请输入楼栋名称" />
                      </el-form-item>
                    </el-col>
                    <el-col :span="12">
                      <el-form-item label="报告编号" :required="true">
                        <el-input v-model="currentForm.buildings[selectedBuildingIndex].code" :readonly="!isEditMode"
                          placeholder="请输入报告编号" />
                      </el-form-item>
                    </el-col>
                  </el-row>
                  <el-row :gutter="20">
                    <el-col :span="12">
                      <el-form-item label="地上层数">
                        <div style="display: flex; align-items: center; gap: 8px;">
                          <el-input-number v-model="currentForm.buildings[selectedBuildingIndex].floors"
                            :readonly="!isEditMode" :min="1" :max="100" :precision="0" :controls="false"
                            style="flex: 1;" @change="handleBuildingFloorChange" />
                          <el-button
                            :disabled="!isEditMode || currentForm.buildings[selectedBuildingIndex].floors >= 100"
                            @click="incrementFloor" type="primary" :icon="Plus" size="small" circle />
                          <el-button :disabled="!isEditMode || currentForm.buildings[selectedBuildingIndex].floors <= 1"
                            @click="decrementFloor" type="primary" :icon="Minus" size="small" circle />
                        </div>
                      </el-form-item>
                    </el-col>
                    <el-col :span="12">
                      <el-form-item label="地下层数">
                        <div style="display: flex; align-items: center; gap: 8px;">
                          <el-input-number v-model="currentForm.buildings[selectedBuildingIndex].basementFloors"
                            :readonly="!isEditMode" :min="0" :max="50" :precision="0" :controls="false"
                            style="flex: 1;" @change="handleBasementFloorChange" />
                          <el-button
                            :disabled="!isEditMode || currentForm.buildings[selectedBuildingIndex].basementFloors >= 50"
                            @click="incrementBasementFloor" type="primary" :icon="Plus" size="small" circle />
                          <el-button
                            :disabled="!isEditMode || currentForm.buildings[selectedBuildingIndex].basementFloors <= 0"
                            @click="decrementBasementFloor" type="primary" :icon="Minus" size="small" circle />
                        </div>
                      </el-form-item>
                    </el-col>
                  </el-row>
                  <div class="floor-documents-section">
                    <div class="floor-documents-container" v-if="currentFloorDocuments.length > 0">
                      <div class="floor-list">
                        <div v-for="(doc, floorIndex) in currentFloorDocuments" :key="doc.floorNumber"
                          :class="['floor-list-item', { active: selectedFloorIndex === floorIndex }]"
                          @click="selectFloor(floorIndex)">
                          {{ doc.floorNumber }} 层
                        </div>
                      </div>
                      <div class="floor-upload-panel" v-if="selectedFloorDocument">
                        <div class="floor-upload-header">
                          <h5>{{ selectedFloorDocument.floorNumber }} 层图纸</h5>
                        </div>

                        <!-- 上传进度显示 -->
                        <div v-if="floorUploading" class="upload-progress-container">
                          <el-progress type="circle" :percentage="floorUploadProgress" :width="80" :stroke-width="6">
                            <template #default="{ percentage }">
                              <span class="progress-text">{{ percentage }}%</span>
                            </template>
                          </el-progress>
                          <div class="progress-label">上传中...</div>
                        </div>

                        <el-upload v-if="!floorUploading && can('图片上传')" class="floor-upload" drag action="#"
                          :file-list="selectedFloorDocument.files" :auto-upload="false"
                          :on-change="handleFloorFileChange" :on-remove="handleFloorFileRemove"
                          :disabled="!isEditMode || floorUploading" :limit="1" :on-exceed="handleExceed"
                          accept="image/*,.pdf,application/pdf"
                          :show-file-list="false">
                          <el-icon class="upload-icon">
                            <UploadFilled />
                          </el-icon>
                          <div class="el-upload__text" v-if="selectedFloorDocument.files.length === 0">
                            {{ isEditMode ? '拖拽或点击上传图纸' : '查看模式，无法上传图纸' }}
                          </div>
                          <div class="el-upload__tip" v-if="selectedFloorDocument.files.length === 0">
                            {{ isEditMode ? '每层只能上传一张图纸，文件暂存于当前页面，保存后统一提交' : '请切换到编辑模式进行图纸上传' }}
                          </div>
                        </el-upload>

                        <!-- 显示当前已上传的图纸信息 -->
                        <div v-if="selectedFloorDocument.files.length > 0" class="uploaded-files-info">
                          <div class="files-header">
                            <span>已上传图纸 ({{ selectedFloorDocument.files.length }}个)</span>
                          </div>
                          <div class="files-list">
                            <div v-for="(file, fileIndex) in selectedFloorDocument.files" :key="file.uid"
                              class="file-item">
                              <div class="file-content" @click="previewImage(file)">
                                <div class="file-thumbnail-wrapper" v-loading="(file as any).loading">
                                  <!-- PDF显示缩略图（首页渲染） -->
                                  <template v-if="(file as any).isPdf">
                                    <img v-if="(file as any).thumbnailUrl" :src="(file as any).thumbnailUrl"
                                      class="file-thumbnail" alt="PDF缩略图" />
                                    <div v-else class="pdf-thumbnail">
                                      <el-icon :size="32" color="#f56c6c">
                                        <Document />
                                      </el-icon>
                                      <span class="pdf-label">PDF</span>
                                    </div>
                                  </template>
                                  <!-- 图片显示缩略图 -->
                                  <img v-else-if="(file as any).url" :src="(file as any).url" class="file-thumbnail"
                                    alt="缩略图" />
                                  <el-icon v-else>
                                    <Document />
                                  </el-icon>
                                </div>
                                <span class="file-name">{{ file.name }}</span>
                                <el-tag size="small" type="success">已上传</el-tag>
                              </div>
                              <el-button v-if="can('图片上传')" type="danger" size="small" :icon="Close" circle
                                @click="removeFloorFile(fileIndex)" :disabled="!isEditMode" class="file-remove-btn">
                              </el-button>
                            </div>
                          </div>
                        </div>
                        <div style="margin-top: 10px;">
                          <el-button v-if="can('图片上传')" type="primary" @click="batchUpload" :disabled="!isEditMode">
                            批量上传文件夹
                          </el-button>
                          <div class="el-upload__tip" style="margin-top: 5px;">
                            请按楼层号命名文件，如-2.jpg、-1.jpg、1.jpg、2.pdf，每层只能上传一张图纸
                          </div>
                        </div>
                      </div>
                    </div>
                    <div v-else class="floor-documents-empty">
                      暂无楼层信息，请先设置层数
                    </div>
                  </div>

                </div>

                <!-- 操作按钮区域 -->
                <div v-if="isEditMode" class="action-buttons">
                  <div class="action-buttons-row">
                    <el-button type="primary" @click="openProjectDialog">选择原始记录</el-button>
                  </div>

                </div>

                <!-- 已选择的原始记录标签展示 -->
                <div v-if="selectedProjectsData.length > 0" class="selected-projects-tags">
                  <div class="tags-title">已选择的原始记录：</div>
                  <div class="tags-container">
                    <div
                      v-for="(project, projectIndex) in selectedProjectsData"
                      :key="'proj_' + (project.Id || project.id)"
                      class="project-tag-wrapper"
                      :class="{ 'is-dragging': draggedProjectIndex === projectIndex }"
                      :draggable="isEditMode"
                      @dragstart="handleProjectTagDragStart(projectIndex, $event)"
                      @dragover.prevent
                      @drop.prevent="handleProjectTagDrop(projectIndex)"
                      @dragend="draggedProjectIndex = null"
                    >
                      <el-tag
                        :closable="isEditMode"
                        @close="removeSelectedProject(project.Id || project.id)"
                        type="primary"
                        class="project-tag"
                      >
                        {{ project.名称 || project.name || project.项目名称 || `项目${project.Id || project.id}` }}
                      </el-tag>
                    </div>
                  </div>
                </div>
                <div class="action-buttons-row action-buttons-right" v-if="isEditMode">
                  <el-button v-if="can('保存')" type="primary" @click="saveFormData" :loading="saveLoading">保存数据</el-button>
                  <el-button @click="resetForm" :disabled="saveLoading">重置</el-button>
                </div>
              </div>
            </el-form>
          </div>

          <div v-else class="empty-state">
            <el-empty description="请先创建或选择一个表单" />
          </div>
        </div>
      </div>
    </el-card>
    <!-- 原始记录选择弹框 -->
    <el-dialog v-model="dialogVisible" title="选择原始记录" width="70%" top="50px" :close-on-click-modal="false">
      <el-tabs v-model="activeTab" @tab-change="handleTabChange">
        <!-- 原始记录包Tab -->
        <el-tab-pane label="原始记录包" name="package">
          <DynamicTable :data="packageListData" :model="packageTableModel" :loading="packageListLoading"
            :show-column-selector="true" :show-export="false" :show-detail-button="true" :show-edit-button="false"
            :show-delete-button="false" @detail="handlePackageDetail" @filter="handlePackageFilter"
            :showSelection="true" :defaultSelection="tempSelectedPackagesData"
            @selection-change="handlePackageSelectionChange" height="400" />
        </el-tab-pane>

        <!-- 原始记录Tab -->
        <el-tab-pane label="原始记录" name="project">
          <DynamicTable :data="projectListData" :model="projectTableModel" :loading="projectListLoading"
            :show-column-selector="true" :show-export="false" :show-edit-button="false" :show-delete-button="false"
            @filter="handleProjectFilter" :showSelection="true" :defaultSelection="tempSelectedProjectsData"
            @selection-change="handleProjectSelectionChange" height="400" />
        </el-tab-pane>
      </el-tabs>

      <template #footer>
        <span class="dialog-footer">
          <!-- <el-button @click="closeProjectDialog(true)">取消</el-button> -->
          <el-button type="primary" @click="confirmProjectSelection">确定</el-button>
        </span>
      </template>
    </el-dialog>

    <!-- 原始记录包详情弹框 -->
    <el-dialog v-model="packageDetailVisible" title="原始记录包详情" width="60%" top="50px" :close-on-click-modal="false">
      <div v-if="currentPackageDetail" class="package-detail-content">
        <div class="package-info">
          <h3>{{ currentPackageDetail.名称 || currentPackageDetail.name || '项目包' }}</h3>
          <p v-if="currentPackageDetail.说明 || currentPackageDetail.description" class="package-description">
            {{ currentPackageDetail.说明 || currentPackageDetail.description }}
          </p>
        </div>
        <el-divider />
        <div class="package-projects">
          <h4>包含的原始记录：</h4>
          <DynamicTable :data="packageDetailProjects" :model="packageDetailProjectsModel"
            :loading="packageDetailLoading" :show-column-selector="true" :show-export="false"
            :show-detail-button="false" :show-edit-button="false" :show-delete-button="false" :showSelection="false"
            height="350" />
        </div>
      </div>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="packageDetailVisible = false">关闭</el-button>
        </span>
      </template>
    </el-dialog>

    <!-- 当前项目文件弹框 -->
    <el-dialog v-model="projectFileDialogVisible" title="当前文件" width="85%" top="50px" :close-on-click-modal="false">
      <div class="project-file-toolbar">
        <el-button type="danger" :icon="Delete" plain :loading="clearingProjectFiles" @click="clearProjectFiles">
          清空项目文件夹
        </el-button>
      </div>
      <el-table :data="projectFiles" v-loading="projectFilesLoading" border stripe max-height="600" style="width: 100%;">
        <el-table-column type="index" label="序号" width="70" align="center" />
        <el-table-column prop="文件名" label="文件名" min-width="240" show-overflow-tooltip />
        <el-table-column prop="文件类型" label="文件类型" width="100" align="center" />
        <!-- <el-table-column prop="相对路径" label="相对路径" min-width="220" show-overflow-tooltip /> -->
        <el-table-column label="大小" width="120" align="right">
          <template #default="{ row }">
            {{ formatFileSize(row.大小) }}
          </template>
        </el-table-column>
        <el-table-column prop="上传时间" label="上传时间" width="170" />
        <el-table-column prop="上传人" label="上传人" width="100" />
        <el-table-column label="操作" width="210" fixed="right" align="center">
          <template #default="{ row }">
            <el-button type="primary" size="small" :icon="Download" plain :loading="downloadingFileId === String(row.Id || '')"
              @click="downloadProjectFile(row)">
              下载
            </el-button>
            <el-button type="danger" size="small" :icon="Delete" plain :loading="deletingFileId === String(row.Id || '')"
              @click="deleteProjectFile(row)">
              删除
            </el-button>
          </template>
        </el-table-column>
      </el-table>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="projectFileDialogVisible = false">关闭</el-button>
        </span>
      </template>
    </el-dialog>

    <el-dialog v-model="projectFileUploadDialogVisible" width="680px" :close-on-click-modal="false"
      class="project-file-upload-dialog" @closed="resetProjectFileUpload">
      <template #header>
        <div class="project-file-upload-dialog-title">
          <span class="project-file-upload-title-icon"><el-icon><Upload /></el-icon></span>
          <span>文件上传</span>
        </div>
      </template>
      <div class="project-file-upload-content">
        <el-upload v-model:file-list="projectUploadFileList" action="#" multiple :auto-upload="false" drag
          :show-file-list="false" :disabled="projectFilesUploading" class="project-file-upload-zone">
          <span class="project-file-upload-zone-icon"><el-icon><UploadFilled /></el-icon></span>
          <div class="project-file-upload-zone-title">选择文件</div>
          <div class="project-file-upload-zone-tip">支持一次选择多个文件</div>
        </el-upload>

        <div v-if="projectUploadFileList.length" class="project-upload-file-list">
          <div class="project-upload-file-list-header">
            <span>待上传文件</span>
            <div class="project-upload-file-list-actions">
              <span>{{ projectUploadFileList.length }} 个</span>
              <el-button text type="danger" :icon="Delete" title="清空待上传文件" :disabled="projectFilesUploading"
                @click="clearProjectUploadFiles" />
            </div>
          </div>
          <div v-for="file in projectUploadFileList" :key="file.uid" class="project-upload-file-item">
            <span class="project-upload-file-icon"><el-icon><Document /></el-icon></span>
            <span class="project-upload-file-name" :title="file.name">{{ file.name }}</span>
            <el-button text :icon="Close" title="移除文件" :disabled="projectFilesUploading"
              @click="removeProjectUploadFile(file)" />
          </div>
        </div>
      </div>
      <template #footer>
        <span class="dialog-footer">
          <el-button :disabled="projectFilesUploading" @click="projectFileUploadDialogVisible = false">取消</el-button>
          <el-button type="primary" :loading="projectFilesUploading" :disabled="!projectUploadFileList.length"
            @click="uploadProjectFiles">确定上传</el-button>
        </span>
      </template>
    </el-dialog>

    <!-- 添加图片/PDF预览对话框 -->
    <el-dialog v-model="imagePreviewVisible" :title="previewIsPdf ? 'PDF预览' : '图片预览'" width="80%" top="50px"
      :destroy-on-close="true">
      <div class="image-preview-container">
        <iframe v-if="previewIsPdf" :src="previewImageUrl" class="preview-pdf" />
        <img v-else-if="previewImageUrl" :src="previewImageUrl" alt="预览图片" class="preview-image" />
        <div v-else class="preview-loading">加载中...</div>
      </div>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="imagePreviewVisible = false">关闭</el-button>
        </span>
      </template>
    </el-dialog>

    <!-- 人员选择弹框 -->
    <el-dialog v-model="teamDialogVisible" :title="memberDialogTitle" width="75%" top="50px" :close-on-click-modal="false"
      class="team-dialog">
      <div class="team-dialog-content">
        <!-- 左侧部门树 -->
        <div class="team-left-panel">
          <div class="team-panel-header">
            <span class="team-panel-title">部门结构</span>
            <el-button
              type="primary"
              :icon="Refresh"
              circle
              size="small"
              class="app-refresh-btn"
              @click="loadTeamDepartmentTree"
              :loading="teamTreeLoading"
              title="刷新"
            />
          </div>
          <div class="team-search-box">
            <el-input v-model="teamFilterText" placeholder="搜索部门" :prefix-icon="Search" clearable size="default"
              @keyup.enter="handleTeamTreeFilter" @clear="handleTeamTreeFilter" />
          </div>
          <div class="team-tree-wrapper" v-loading="teamTreeLoading">
            <el-tree ref="teamTreeRef" :data="teamDepartmentTree" :props="teamTreeProps" node-key="value"
              :filter-node-method="filterTeamNode" :expand-on-click-node="false" highlight-current
              @node-click="handleTeamNodeClick">
              <template #default="{ node }">
                <span class="tree-node">
                  <el-icon class="node-icon">
                    <OfficeBuilding />
                  </el-icon>
                  <span class="node-label">{{ node.label }}</span>
                </span>
              </template>
            </el-tree>
          </div>
          <div class="team-tree-options">
            <el-checkbox v-model="teamShowSubUsers" @change="handleTeamShowSubChange">显示下级人员</el-checkbox>
          </div>
        </div>

        <!-- 右侧人员列表 -->
        <div class="team-right-panel" v-loading="teamUserLoading">
          <div class="team-panel-header">
            <span class="team-panel-title">
              {{ teamCurrentDepartment ? teamCurrentDepartment.label : '请选择部门' }}
            </span>
          </div>
          <div v-if="teamCurrentDepartment" class="team-user-content">
            <div class="team-filter-bar">
              <el-input v-model="teamUserSearchText" placeholder="搜索人员姓名" :prefix-icon="Search" clearable
                style="width: 240px;" @clear="handleTeamUserSearch" @keyup.enter="handleTeamUserSearch" />
              <el-button type="primary" size="default" @click="handleTeamUserSearch">搜索</el-button>
            </div>
            <div class="team-table-wrapper">
              <el-table :data="teamUserList" stripe border style="width: 100%;" max-height="400">
                <el-table-column width="55" align="center">
                  <template #default="{ row }">
                    <el-checkbox :model-value="isTeamMemberSelected(row)" @change="toggleTeamMember(row)" />
                  </template>
                </el-table-column>
                <el-table-column type="index" label="序号" width="60" align="center" />
                <el-table-column prop="uRealName" label="姓名" min-width="100" />
                <el-table-column prop="uLoginName" label="账号" min-width="130" />
                <el-table-column prop="DepartmentName" label="部门" min-width="150" />
              </el-table>
            </div>
          </div>
          <div v-else class="team-empty-state">
            <el-empty description="请在左侧选择一个部门查看人员" />
          </div>

          <!-- 已选成员预览 -->
          <div v-if="Object.keys(tempExecutionTeam).length > 0" class="team-selected-preview">
            <span class="preview-label">已选 {{ Object.keys(tempExecutionTeam).length }} 人：</span>
            <el-tag v-for="(name, code) in tempExecutionTeam" :key="code" closable size="small" type="success"
              @close="toggleTeamMember({ code: code as string, uRealName: name } as TeamUserInfo)" style="margin: 2px;">
              {{ name }}
            </el-tag>
          </div>
        </div>
      </div>
      <template #footer>
        <el-button @click="teamDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="confirmTeamSelection">确定</el-button>
      </template>
    </el-dialog>

    <!-- 主责部门选择弹框 -->
    <el-dialog v-model="deptDialogVisible" title="选择主责部门" width="450px" top="50px" :close-on-click-modal="false"
      class="dept-dialog">
      <div class="dept-dialog-content">
        <div class="dept-search-box">
          <el-input v-model="deptFilterText" placeholder="搜索部门" :prefix-icon="Search" clearable size="default"
            @keyup.enter="handleDeptTreeFilter" @clear="handleDeptTreeFilter" />
        </div>
        <div class="dept-tree-wrapper" v-loading="deptTreeLoading">
          <el-tree ref="deptTreeRef" :data="deptTreeData" :props="deptTreeProps" node-key="value"
            :filter-node-method="filterDeptNode" :expand-on-click-node="false" highlight-current
            @node-click="handleDeptNodeClick">
            <template #default="{ node }">
              <span class="tree-node">
                <el-icon class="node-icon">
                  <OfficeBuilding />
                </el-icon>
                <span class="node-label">{{ node.label }}</span>
              </span>
            </template>
          </el-tree>
        </div>
      </div>
      <template #footer>
        <el-button @click="deptDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="confirmDeptSelection">确定</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from 'vue';
import { Plus, Edit, Close, Upload, UploadFilled, Refresh, Minus, Document, View, Download, Delete } from '@element-plus/icons-vue';
import { ElMessage, ElMessageBox, UploadProps, UploadUserFile, genFileId } from 'element-plus';
import DynamicTable from '@/components/DynamicTable.vue';
import { ParameterSettings, PermissionManagement } from '@/network/index';
import type { ElTree } from 'element-plus';
import { Search, OfficeBuilding } from '@element-plus/icons-vue';
import { useButtonPermission } from '@/utils/buttonPermission';

const { can, canAny } = useButtonPermission('/ParameterSettings/BusinessScope/index.vue');

let pdfjsLibPromise: Promise<any> | null = null;

const getPdfjsLib = async () => {
  if (!pdfjsLibPromise) {
    pdfjsLibPromise = import('pdfjs-dist/legacy/build/pdf').then(module => {
      module.GlobalWorkerOptions.workerSrc = '//cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.worker.min.js';
      return module;
    });
  }
  return pdfjsLibPromise;
};

interface FloorDocument {
  floorNumber: number;
  files: UploadUserFile[];
}

interface Building {
  id: string;
  code: string;
  name: string;
  floors: number; // 地上层数
  basementFloors: number; // 地下层数（0 表示无地下层）
  selected: boolean;
  floorDocuments: FloorDocument[];
  editing?: boolean;
}

interface FormData {
  id?: number;
  code: string;
  name: string;
  projectManagerCode?: string;
  projectManager: string;
  responsibleDepartment: string;
  status?: string;
  startTime: string | null;
  remark: string;
  buildingCount: number;
  buildings: Building[];
  projectAssistant: Record<string, string>;
  executionTeam: Record<string, string>;
}
// 添加用于存储缩略图URL的Map
const thumbnailCache = new Map<string, string>(); // key: serverCode, value: thumbnail URL

// 左侧面板拖拽调整宽度相关
const mainContentRef = ref<HTMLElement | null>(null);
const leftPanelRef = ref<HTMLElement | null>(null);
const leftPanelWidth = ref<number>(0); // 初始为0，onMounted时设置为容器一半
const isResizing = ref<boolean>(false);
const minPanelWidth = 200;

const startResize = (e: MouseEvent) => {
  isResizing.value = true;
  document.addEventListener('mousemove', handleResizePanel);
  document.addEventListener('mouseup', stopResize);
  document.body.style.cursor = 'col-resize';
  document.body.style.userSelect = 'none';
};

const handleResizePanel = (e: MouseEvent) => {
  if (!isResizing.value || !mainContentRef.value) return;
  const containerRect = mainContentRef.value.getBoundingClientRect();
  const maxWidth = containerRect.width / 2;
  let newWidth = e.clientX - containerRect.left;
  newWidth = Math.max(minPanelWidth, Math.min(maxWidth, newWidth));
  leftPanelWidth.value = newWidth;
};

const stopResize = () => {
  isResizing.value = false;
  document.removeEventListener('mousemove', handleResizePanel);
  document.removeEventListener('mouseup', stopResize);
  document.body.style.cursor = '';
  document.body.style.userSelect = '';
};

onUnmounted(() => {
  document.removeEventListener('mousemove', handleResizePanel);
  document.removeEventListener('mouseup', stopResize);
});

const activeFormIndex = ref<number>(-1);
// formList 存储接口返回的原始数据（用于左侧列表展示）
const formList = ref<any[]>([]);
const currentForm = ref<FormData | null>(null);
// 保存数据loading状态
const saveLoading = ref<boolean>(false);
const dynamicTableRef = ref<InstanceType<typeof DynamicTable> | null>(null);
const selectedBuildingIndex = ref<number | null>(null); // 当前选中的楼栋索引
const selectedFloorIndex = ref<number | null>(null); // 当前选中的楼层索引

// 加载状态
const loading = ref<boolean>(false); // 右侧详情区域加载状态
const listLoading = ref<boolean>(false); // 左侧列表加载状态
const quickSyncLoading = ref<boolean>(false); // 快速同步加载状态
const floorUploading = ref<boolean>(false); // 楼层图纸上传中状态
const floorUploadProgress = ref<number>(0); // 楼层图纸上传进度

// 筛选参数
const filterParams = ref<any[]>([]);
const quickKeyword = ref('');

// 分页相关状态
const formPagination = ref({
  show: true,
  page: 1,
  pageSize: 10,
  total: 0,
  pageSizes: [10, 20, 50, 100],
  layout: 'total, sizes, prev, pager, next, jumper',
  small: true
});

// model数据，用于表格表头展示
const tableModel = ref<Record<string, string> | undefined>(undefined);

// 编辑模式控制
const isEditMode = ref<boolean>(false);
// 当前选中数据是否允许编辑（由接口 editable 字段控制）
const currentFormEditable = ref<boolean>(false);
const canEnterEditMode = computed(() => !!currentForm.value && currentFormEditable.value);

const toEditableFlag = (value: unknown, fallback = false): boolean => {
  if (value === undefined || value === null) return fallback;
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value === 1;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    return normalized === 'true' || normalized === '1';
  }
  return fallback;
};

// 表单引用
const formRef = ref<any>(null);

// 表单验证规则
const formRules = {
  buildingCount: [
    { required: true, message: '请输入建筑个数', trigger: 'change' }
  ]
};

// 辅助函数：创建默认表单数据
const createEmptyForm = (): FormData => ({
  code: '',
  name: '',
  projectManagerCode: '',
  projectManager: '',
  responsibleDepartment: '',
  status: '',
  startTime: null,
  remark: '',
  buildingCount: 0,
  buildings: [],
  projectAssistant: {},
  executionTeam: {}
});


// 弹窗相关的变量
const dialogVisible = ref(false);
const activeTab = ref('package'); // 当前激活的Tab页：'package' 或 'project'

// 检测项目相关
const projectListData = ref<any[]>([]);
const projectTableModel = ref<Record<string, string> | undefined>(undefined);
const projectListLoading = ref(false);
const projectFilterParams = ref<any[]>([]);
const selectedProjectIds = ref<any[]>([]);
// 临时选择的项目ID（弹框内使用，点击确定后才同步到selectedProjectIds）
const tempSelectedProjectIds = ref<any[]>([]);
// 保存已选项目的完整数据（用于查看模式显示）
const selectedProjectsFullData = ref<any[]>([]);
const draggedProjectIndex = ref<number | null>(null);

const getProjectItemId = (item: any) => item?.Id ?? item?.id;
const getProjectIdKey = (id: any) => String(id);

// 检测项目包相关
const packageListData = ref<any[]>([]);
const packageTableModel = ref<Record<string, string> | undefined>(undefined);
const packageListLoading = ref(false);
const packageFilterParams = ref<any[]>([]);
const selectedPackageIds = ref<any[]>([]);
// 临时选择的项目包ID
const tempSelectedPackageIds = ref<any[]>([]);
// 保存已选项目包的完整数据
const selectedPackagesFullData = ref<any[]>([]);

// 检测项目包详情弹框相关
const packageDetailVisible = ref(false);
const currentPackageDetail = ref<any>(null);
const packageDetailProjects = ref<any[]>([]);
const packageDetailProjectsModel = ref<Record<string, string> | undefined>(undefined);
const packageDetailLoading = ref(false);

interface ProjectFileItem {
  项目编码?: string;
  相对路径?: string;
  文件名?: string;
  文件类型?: string;
  哈希值?: string;
  大小?: string | number;
  上传时间?: string;
  上传人?: string;
  Id?: string | number;
}

const projectFileDialogVisible = ref(false);
const projectFiles = ref<ProjectFileItem[]>([]);
const projectFilesLoading = ref(false);
const projectFilesLoadedProjectId = ref<string>('');
const downloadingFileId = ref<string>('');
const deletingFileId = ref<string>('');
const clearingProjectFiles = ref(false);
const projectFileUploadDialogVisible = ref(false);
const projectUploadFileList = ref<UploadUserFile[]>([]);
const projectFilesUploading = ref(false);
const projectFileCount = computed(() => projectFiles.value.length);

const getCurrentOperatingProjectId = (): string => {
  const formId = currentForm.value?.id;
  if (formId !== undefined && formId !== null && formId !== 0) return String(formId);
  if (activeFormIndex.value < 0 || activeFormIndex.value >= formList.value.length) return '';
  const item = formList.value[activeFormIndex.value];
  const itemId = item?.Id ?? item?.id;
  return itemId !== undefined && itemId !== null ? String(itemId) : '';
};

const resetProjectFiles = () => {
  projectFiles.value = [];
  projectFilesLoadedProjectId.value = '';
  downloadingFileId.value = '';
  deletingFileId.value = '';
  clearingProjectFiles.value = false;
  projectFilesLoading.value = false;
};

const loadProjectFiles = async (projectId = getCurrentOperatingProjectId(), showError = projectFileDialogVisible.value) => {
  if (!projectId) {
    resetProjectFiles();
    return;
  }

  try {
    projectFilesLoading.value = true;
    const res: any = await ParameterSettings.operatingProjectFileStructure({ 项目id: projectId });
    const files = res?.status === 200 && res?.success && Array.isArray(res.response)
      ? res.response
      : [];

    if (getCurrentOperatingProjectId() === projectId) {
      projectFiles.value = files;
      projectFilesLoadedProjectId.value = projectId;
    }
  } catch (error) {
    console.error('加载当前项目文件失败:', error);
    if (getCurrentOperatingProjectId() === projectId) {
      projectFiles.value = [];
      projectFilesLoadedProjectId.value = projectId;
    }
    if (showError) {
      ElMessage.error('当前项目文件加载失败');
    }
  } finally {
    if (getCurrentOperatingProjectId() === projectId) {
      projectFilesLoading.value = false;
    }
  }
};

const openProjectFileDialog = async () => {
  const projectId = getCurrentOperatingProjectId();
  if (!projectId) {
    ElMessage.warning('请先选择一个经营项目');
    return;
  }

  projectFileDialogVisible.value = true;
  await loadProjectFiles(projectId, true);
};

const openProjectFileUploadDialog = () => {
  if (!getCurrentOperatingProjectId()) {
    ElMessage.warning('请先选择一个经营项目');
    return;
  }

  projectUploadFileList.value = [];
  projectFileUploadDialogVisible.value = true;
};

const resetProjectFileUpload = () => {
  projectUploadFileList.value = [];
  projectFilesUploading.value = false;
};

const removeProjectUploadFile = (file: UploadUserFile) => {
  projectUploadFileList.value = projectUploadFileList.value.filter(item => item.uid !== file.uid);
};

const clearProjectUploadFiles = () => {
  projectUploadFileList.value = [];
};

const uploadProjectFiles = async () => {
  const projectId = getCurrentOperatingProjectId();
  const files = projectUploadFileList.value.filter(file => file.raw instanceof File);
  if (!projectId) {
    ElMessage.warning('请先选择一个经营项目');
    return;
  }
  if (!files.length) {
    ElMessage.warning('请选择要上传的文件');
    return;
  }

  try {
    projectFilesUploading.value = true;
    const form = new FormData();
    files.forEach(file => form.append('文件列表', file.raw as File, file.name));
    await ParameterSettings.operatingProjectFileUpload({ 项目id: projectId }, form);
    ElMessage.success(`成功上传 ${files.length} 个文件`);
    projectFileUploadDialogVisible.value = false;
    await loadProjectFiles(projectId, true);
  } catch (error) {
    console.error('上传当前项目文件失败:', error);
    ElMessage.error('文件上传失败');
  } finally {
    projectFilesUploading.value = false;
  }
};

const formatFileSize = (value: string | number | undefined): string => {
  const size = Number(value);
  if (!Number.isFinite(size) || size < 0) return '-';
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  if (size < 1024 * 1024 * 1024) return `${(size / 1024 / 1024).toFixed(1)} MB`;
  return `${(size / 1024 / 1024 / 1024).toFixed(1)} GB`;
};

const downloadBlobFile = (blob: Blob, fileName: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName || '项目文件';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const downloadProjectFile = async (row: ProjectFileItem) => {
  const projectId = getCurrentOperatingProjectId();
  const fileId = row.Id !== undefined && row.Id !== null ? String(row.Id) : '';
  if (!projectId || !fileId) {
    ElMessage.warning('缺少项目id或文件id，无法下载');
    return;
  }

  try {
    downloadingFileId.value = fileId;
    const res: any = await ParameterSettings.operatingProjectFileDownload({
      项目id: projectId,
      文件id: fileId
    });
    const blob = getDownloadBlob(res);
    if (!blob) {
      ElMessage.error('下载失败，未获取到文件内容');
      return;
    }
    downloadBlobFile(blob, row.文件名 || row.相对路径 || `项目文件_${fileId}`);
    ElMessage.success('下载成功');
  } catch (error) {
    console.error('下载当前项目文件失败:', error);
    ElMessage.error('下载失败');
  } finally {
    downloadingFileId.value = '';
  }
};

const deleteProjectFile = async (row: ProjectFileItem) => {
  const projectId = getCurrentOperatingProjectId();
  const fileId = row.Id !== undefined && row.Id !== null ? String(row.Id) : '';
  if (!projectId || !fileId) {
    ElMessage.warning('缺少项目id或文件id，无法删除');
    return;
  }

  try {
    await ElMessageBox.confirm(`确定要删除文件“${row.文件名 || row.相对路径 || ''}”吗？`, '提示', {
      confirmButtonText: '删除',
      cancelButtonText: '取消',
      type: 'warning'
    });
    deletingFileId.value = fileId;
    await ParameterSettings.operatingProjectFileDelete({ 项目id: projectId, 文件id: fileId });
    ElMessage.success('删除成功');
    await loadProjectFiles(projectId, true);
  } catch (error) {
    if (error !== 'cancel' && error !== 'close') {
      console.error('删除当前项目文件失败:', error);
      ElMessage.error('删除失败');
    }
  } finally {
    deletingFileId.value = '';
  }
};

const clearProjectFiles = async () => {
  const projectId = getCurrentOperatingProjectId();
  if (!projectId) {
    ElMessage.warning('请先选择一个经营项目');
    return;
  }

  try {
    await ElMessageBox.confirm('确认要完全清除文件夹吗？', '危险操作', {
      confirmButtonText: '确认清空',
      cancelButtonText: '取消',
      type: 'error'
    });
    clearingProjectFiles.value = true;
    await ParameterSettings.operatingProjectFileClear({ 项目id: projectId });
    ElMessage.success('项目文件夹已清空');
    await loadProjectFiles(projectId, true);
  } catch (error) {
    if (error !== 'cancel' && error !== 'close') {
      console.error('清空项目文件夹失败:', error);
      ElMessage.error('清空项目文件夹失败');
    }
  } finally {
    clearingProjectFiles.value = false;
  }
};

// ========== 执行团队选择弹框相关 ==========
interface DepartmentNode {
  value: string;
  Pid: string;
  label: string;
  order: number;
  disabled: boolean;
  children: DepartmentNode[] | null;
}

interface TeamUserInfo {
  uID?: string;
  code?: string;
  uLoginName?: string;
  uRealName?: string;
  DepartmentName?: string;
  DepartmentId?: string;
}

type MemberSelectTarget = 'manager' | 'assistant' | 'execution';

const teamDialogVisible = ref(false);
const memberSelectTarget = ref<MemberSelectTarget>('execution');
const teamTreeRef = ref<InstanceType<typeof ElTree>>();
const teamDepartmentTree = ref<DepartmentNode[]>([]);
const teamTreeLoading = ref(false);
const teamUserList = ref<TeamUserInfo[]>([]);
const teamUserLoading = ref(false);
const teamCurrentDepartment = ref<DepartmentNode | null>(null);
const teamFilterText = ref('');
const teamUserSearchText = ref('');
const teamShowSubUsers = ref(false);
// 弹框内临时选中的成员 {code: uRealName}
const tempExecutionTeam = ref<Record<string, string>>({});

// ========== 主责部门选择弹框相关 ==========
const deptDialogVisible = ref(false);
const deptTreeRef = ref<InstanceType<typeof ElTree>>();
const deptTreeData = ref<DepartmentNode[]>([]);
const deptTreeLoading = ref(false);
const deptFilterText = ref('');
const deptSelectedNode = ref<DepartmentNode | null>(null);
const deptTreeProps = {
  children: 'children',
  label: 'label',
  disabled: 'disabled'
};

const memberDialogTitle = computed(() => {
  if (memberSelectTarget.value === 'manager') return '选择项目负责人';
  if (memberSelectTarget.value === 'assistant') return '选择项目助理';
  return '选择执行团队成员';
});

const getTeamUserCode = (user: TeamUserInfo): string => {
  return String(user.code || user.uID || user.uLoginName || '').trim();
};

const getTeamUserName = (user: TeamUserInfo): string => {
  return String(user.uRealName || user.uLoginName || getTeamUserCode(user)).trim();
};

const teamTreeProps = {
  children: 'children',
  label: 'label',
  disabled: 'disabled'
};

// 打开人员选择弹框
const openMemberDialog = async (target: MemberSelectTarget) => {
  memberSelectTarget.value = target;
  if (target === 'manager') {
    const managerName = currentForm.value?.projectManager;
    const managerCode = currentForm.value?.projectManagerCode;
    tempExecutionTeam.value = managerName
      ? { [managerCode || managerName]: managerName }
      : {};
  } else if (target === 'assistant') {
    tempExecutionTeam.value = currentForm.value
      ? { ...currentForm.value.projectAssistant }
      : {};
  } else {
    tempExecutionTeam.value = currentForm.value
      ? { ...currentForm.value.executionTeam }
      : {};
  }
  teamDialogVisible.value = true;
  teamCurrentDepartment.value = null;
  teamUserList.value = [];
  teamFilterText.value = '';
  teamUserSearchText.value = '';
  if (teamDepartmentTree.value.length === 0) {
    await loadTeamDepartmentTree();
  }
};

// 加载部门树
const loadTeamDepartmentTree = async () => {
  try {
    teamTreeLoading.value = true;
    const res: Record<string, unknown> = await PermissionManagement.getDepartmentTree({ pid: '0' });
    const resAny = res as Record<string, unknown>;
    if (resAny && (resAny as Record<string, unknown>).status === 200 && (resAny as Record<string, unknown>).success) {
      const response = (resAny as Record<string, unknown>).response as Record<string, unknown>;
      if (response?.children && Array.isArray(response.children)) {
        teamDepartmentTree.value = response.children as DepartmentNode[];
      } else {
        teamDepartmentTree.value = response ? [response as unknown as DepartmentNode] : [];
      }
    } else {
      teamDepartmentTree.value = [];
    }
  } catch (error) {
    console.error('加载部门树失败:', error);
    teamDepartmentTree.value = [];
  } finally {
    teamTreeLoading.value = false;
  }
};

// 部门树节点点击
const handleTeamNodeClick = (data: DepartmentNode) => {
  teamCurrentDepartment.value = data;
  teamUserSearchText.value = '';
  loadTeamUserList();
};

// 部门树搜索
const handleTeamTreeFilter = () => {
  teamTreeRef.value?.filter(teamFilterText.value);
};

const filterTeamNode = (value: string, data: DepartmentNode) => {
  if (!value) return true;
  return data.label.includes(value);
};

// 显示下级人员切换
const handleTeamShowSubChange = () => {
  if (teamCurrentDepartment.value) {
    loadTeamUserList();
  }
};

// 人员搜索
const handleTeamUserSearch = () => {
  loadTeamUserList();
};

// 加载人员列表
const loadTeamUserList = async () => {
  if (!teamCurrentDepartment.value) return;
  try {
    teamUserLoading.value = true;
    const res: Record<string, unknown> = await PermissionManagement.getDepartmentUser({
      nameKey: teamUserSearchText.value || undefined,
      departmentId: teamCurrentDepartment.value.value,
      includesSubordinates: teamShowSubUsers.value,
    });
    const resAny = res as Record<string, unknown>;
    if (resAny && (resAny as Record<string, unknown>).status === 200 && (resAny as Record<string, unknown>).success) {
      const response = (resAny as Record<string, unknown>).response as Record<string, unknown>;
      teamUserList.value = Array.isArray(response?.data) ? (response.data as TeamUserInfo[]) : [];
    } else {
      teamUserList.value = [];
    }
  } catch (error) {
    console.error('加载人员列表失败:', error);
    teamUserList.value = [];
  } finally {
    teamUserLoading.value = false;
  }
};

// 判断人员是否已选中
const isTeamMemberSelected = (user: TeamUserInfo): boolean => {
  const code = getTeamUserCode(user);
  return !!code && !!tempExecutionTeam.value[code];
};

// 切换人员选中状态
const toggleTeamMember = (user: TeamUserInfo) => {
  const code = getTeamUserCode(user);
  const name = getTeamUserName(user);
  if (!code) {
    ElMessage.warning('当前人员缺少编码，无法选择');
    return;
  }

  if (tempExecutionTeam.value[code]) {
    const { [code]: _, ...rest } = tempExecutionTeam.value;
    tempExecutionTeam.value = rest;
  } else {
    if (memberSelectTarget.value === 'manager') {
      tempExecutionTeam.value = { [code]: name };
    } else {
      tempExecutionTeam.value = { ...tempExecutionTeam.value, [code]: name };
    }
  }
};

// 确认人员选择
const confirmTeamSelection = () => {
  if (!currentForm.value) return;
  if (memberSelectTarget.value === 'manager') {
    const [code, name] = Object.entries(tempExecutionTeam.value)[0] || [];
    currentForm.value.projectManagerCode = code || '';
    currentForm.value.projectManager = name || '';
  } else if (memberSelectTarget.value === 'assistant') {
    currentForm.value.projectAssistant = { ...tempExecutionTeam.value };
  } else {
    currentForm.value.executionTeam = { ...tempExecutionTeam.value };
  }
  teamDialogVisible.value = false;
  ElMessage.success(`已选择 ${Object.keys(tempExecutionTeam.value).length} 名人员`);
};

// ========== 主责部门选择弹框方法 ==========
const openDeptDialog = async () => {
  deptSelectedNode.value = null;
  deptFilterText.value = '';
  deptDialogVisible.value = true;
  if (deptTreeData.value.length === 0) {
    await loadDeptTree();
  }
  nextTick(() => {
    deptTreeRef.value?.filter('');
  });
};

const loadDeptTree = async () => {
  try {
    deptTreeLoading.value = true;
    const res: Record<string, unknown> = await PermissionManagement.getDepartmentTree({ pid: '0' });
    const resAny = res as Record<string, unknown>;
    if (resAny && (resAny as Record<string, unknown>).status === 200 && (resAny as Record<string, unknown>).success) {
      const response = (resAny as Record<string, unknown>).response as Record<string, unknown>;
      if (response?.children && Array.isArray(response.children)) {
        deptTreeData.value = response.children as DepartmentNode[];
      } else {
        deptTreeData.value = response ? [response as unknown as DepartmentNode] : [];
      }
    } else {
      deptTreeData.value = [];
    }
  } catch (error) {
    console.error('加载部门树失败:', error);
    deptTreeData.value = [];
  } finally {
    deptTreeLoading.value = false;
  }
};

const handleDeptNodeClick = (data: DepartmentNode) => {
  deptSelectedNode.value = data;
};

const handleDeptTreeFilter = () => {
  deptTreeRef.value?.filter(deptFilterText.value);
};

const filterDeptNode = (value: string, data: DepartmentNode) => {
  if (!value) return true;
  return data.label.includes(value);
};

const confirmDeptSelection = () => {
  if (!currentForm.value) return;
  if (!deptSelectedNode.value) {
    ElMessage.warning('请先选择一个部门');
    return;
  }
  currentForm.value.responsibleDepartment = deptSelectedNode.value.label;
  deptDialogVisible.value = false;
  ElMessage.success(`已选择主责部门：${deptSelectedNode.value.label}`);
};

// 移除执行团队成员
const removeTeamMember = (code: string) => {
  if (!currentForm.value) return;
  const { [code]: _, ...rest } = currentForm.value.executionTeam;
  currentForm.value.executionTeam = rest;
};

// 移除项目助理
const removeAssistantMember = (code: string) => {
  if (!currentForm.value) return;
  const { [code]: _, ...rest } = currentForm.value.projectAssistant;
  currentForm.value.projectAssistant = rest;
};

// 项目助理列表（与执行团队格式一致）
const assistantList = computed(() => {
  if (!currentForm.value) return [];
  const pa = currentForm.value.projectAssistant;
  if (!pa || typeof pa !== 'object') return [];
  return Object.entries(pa).map(([code, name]) => ({ code, name }));
});

// 格式化执行团队显示文本
const executionTeamList = computed(() => {
  if (!currentForm.value) return [];
  const et = currentForm.value.executionTeam;
  if (!et || typeof et !== 'object') return [];
  return Object.entries(et).map(([code, name]) => ({ code, name }));
});

// 计算已选中项目的完整数据（用于外层标签展示）
// 注意：外层标签不应该依赖 projectListData，因为弹框内筛选会改变 projectListData
// 应该始终使用 selectedProjectsFullData 来展示已确认的选择
const selectedProjectsData = computed(() => {
  if (!Array.isArray(selectedProjectIds.value) || selectedProjectIds.value.length === 0) {
    return []
  }
  const projectsById = new Map<string, any>();
  selectedProjectsFullData.value.forEach((item) => {
    const itemId = getProjectItemId(item);
    if (itemId !== undefined && itemId !== null) {
      projectsById.set(getProjectIdKey(itemId), item);
    }
  });
  return selectedProjectIds.value
    .map((id) => projectsById.get(getProjectIdKey(id)))
    .filter(Boolean);
})

const handleProjectTagDragStart = (index: number, event: DragEvent) => {
  if (!isEditMode.value) return;
  draggedProjectIndex.value = index;
  event.dataTransfer?.setData('text/plain', String(index));
  if (event.dataTransfer) event.dataTransfer.effectAllowed = 'move';
};

const handleProjectTagDrop = (targetIndex: number) => {
  const sourceIndex = draggedProjectIndex.value;
  if (sourceIndex === null || sourceIndex === targetIndex) {
    draggedProjectIndex.value = null;
    return;
  }

  const orderedIds = [...selectedProjectIds.value];
  const [movedId] = orderedIds.splice(sourceIndex, 1);
  orderedIds.splice(targetIndex, 0, movedId);
  selectedProjectIds.value = orderedIds;
  draggedProjectIndex.value = null;
};


// 计算弹框内选中项目的完整数据（用于弹框内表格的defaultSelection）
const tempSelectedProjectsData = computed(() => {
  if (!Array.isArray(tempSelectedProjectIds.value) || tempSelectedProjectIds.value.length === 0) {
    return []
  }
  return projectListData.value.filter(item => {
    const itemId = item.Id || item.id
    return tempSelectedProjectIds.value.includes(itemId)
  })
})

// 计算弹框内选中项目包的完整数据
const tempSelectedPackagesData = computed(() => {
  if (!Array.isArray(tempSelectedPackageIds.value) || tempSelectedPackageIds.value.length === 0) {
    return []
  }
  return packageListData.value.filter(item => {
    const itemId = item.Id || item.id
    return tempSelectedPackageIds.value.includes(itemId)
  })
})

// 图片/PDF预览相关
const imagePreviewVisible = ref(false);
const previewImageUrl = ref('');
const previewIsPdf = ref(false);

// 判断是否为PDF文件
const isPdfFile = (file: UploadUserFile): boolean => {
  const name = file.name?.toLowerCase() || '';
  const type = (file as any).raw?.type || '';
  return name.endsWith('.pdf') || type === 'application/pdf';
};

const isSupportedDrawingFile = (file: { name?: string; type?: string; raw?: File }): boolean => {
  const name = file.name?.toLowerCase() || '';
  const type = file.raw?.type || file.type || '';
  return name.endsWith('.pdf') || type === 'application/pdf'
    || type.startsWith('image/')
    || /\.(jpe?g|png|bmp|webp|gif)$/i.test(name);
};

// 生成PDF首页缩略图
const generatePdfThumbnail = async (file: File): Promise<string> => {
  try {
    const pdfjsLib = await getPdfjsLib();
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
    const page = await pdf.getPage(1);

    // 设置缩略图尺寸
    const scale = 0.5;
    const viewport = page.getViewport({ scale });

    // 创建 canvas
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d')!;
    canvas.width = viewport.width;
    canvas.height = viewport.height;

    // 渲染 PDF 页面到 canvas
    await page.render({
      canvasContext: context,
      viewport: viewport
    }).promise;

    // 转换为图片 URL
    return canvas.toDataURL('image/png');
  } catch (error) {
    console.error('生成PDF缩略图失败:', error);
    return '';
  }
};

// 预览图片/PDF
const previewImage = async (file: UploadUserFile) => {
  try {
    const isPdf = (file as any).isPdf || isPdfFile(file);
    previewIsPdf.value = isPdf;

    // PDF 文件使用 pdfUrl 预览
    if (isPdf && (file as any).pdfUrl) {
      previewImageUrl.value = (file as any).pdfUrl;
      imagePreviewVisible.value = true;
      return;
    }

    // 如果已有url，直接使用
    if ((file as any).url) {
      previewImageUrl.value = (file as any).url;
      imagePreviewVisible.value = true;
      return;
    }

    // 优先使用后端下载接口（基于服务器返回的编码）
    const serverCode = (file as any).serverCode || (file as any).code || (file as any).内容;
    if (serverCode) {
      const url = await fetchImageContent(serverCode);
      if (url) {
        previewImageUrl.value = url;
        // 更新文件的url属性，下次直接使用
        (file as any).url = url;
        imagePreviewVisible.value = true;
        return;
      }
    }

    // 退化处理：使用已有原始文件（本地上传的文件）
    if ((file as any).raw) {
      previewImageUrl.value = URL.createObjectURL((file as any).raw);
      imagePreviewVisible.value = true;
      return;
    }
    ElMessage.warning('无法预览该文件');
  } catch (e) {
    console.error('预览文件失败:', e);
    ElMessage.error('预览文件失败');
  }
};

// 获取图片内容（用于缩略图和预览）
const fetchImageContent = async (serverCode: string): Promise<string | null> => {
  if (!serverCode) return null;

  // Check cache first
  if (thumbnailCache.has(serverCode)) {
    return thumbnailCache.get(serverCode)!;
  }

  try {
    const res: any = await ParameterSettings.operatingProjectDownload({ fileName: serverCode });
    let imageUrl = '';

    if (res instanceof Blob) {
      imageUrl = URL.createObjectURL(res);
    } else {
      // 尝试从不同结构中获取数据
      const data = res?.response || res?.data || res;

      if (data instanceof Blob) {
        imageUrl = URL.createObjectURL(data);
      } else if (typeof data === 'string') {
        if (data.startsWith('http') || data.startsWith('blob:') || data.startsWith('data:')) {
          imageUrl = data;
        } else {
          imageUrl = `data:image/jpeg;base64,${data}`;
        }
      }
    }

    if (imageUrl) {
      thumbnailCache.set(serverCode, imageUrl);
      return imageUrl;
    }
  } catch (e) {
    console.warn(`图片加载失败 (${serverCode}):`, e);
  }
  return null;
};

const getDownloadBlob = (response: any): Blob | null => {
  if (response instanceof Blob) return response;
  const data = response?.response || response?.data || response;
  return data instanceof Blob ? data : null;
};

const isPdfBlob = async (blob: Blob): Promise<boolean> => {
  if (blob.type.toLowerCase().includes('pdf')) return true;
  const header = new Uint8Array(await blob.slice(0, 5).arrayBuffer());
  return String.fromCharCode(...header) === '%PDF-';
};

// 刷新详情时服务端只返回文件编码，需根据实际文件内容重新识别 PDF 并生成缩略图。
const loadFloorDocumentPreview = async (file: any): Promise<void> => {
  const serverCode = String(file?.serverCode || '');
  if (!serverCode) return;

  const response = await ParameterSettings.operatingProjectDownload({ fileName: serverCode });
  const blob = getDownloadBlob(response);
  if (!blob) {
    const url = await fetchImageContent(serverCode);
    if (url) file.url = url;
    return;
  }

  if (await isPdfBlob(blob)) {
    file.isPdf = true;
    file.pdfUrl = URL.createObjectURL(blob);
    file.thumbnailUrl = await generatePdfThumbnail(
      new File([blob], file.name || 'drawing.pdf', { type: blob.type || 'application/pdf' })
    );
    if (!String(file.name || '').toLowerCase().endsWith('.pdf')) {
      file.name = `${String(file.name || '图纸').replace(/\.[^.]+$/, '')}.pdf`;
    }
    return;
  }

  file.isPdf = false;
  file.url = URL.createObjectURL(blob);
  thumbnailCache.set(serverCode, file.url);
};

// 打开弹窗并加载检测项目数据
const openProjectDialog = async () => {
  // 打开弹框时，将当前已确认的选择复制到临时变量
  tempSelectedProjectIds.value = [...selectedProjectIds.value];
  tempSelectedPackageIds.value = [...selectedPackageIds.value];

  dialogVisible.value = true;

  // 根据当前Tab加载对应的数据
  if (activeTab.value === 'package') {
    if (packageListData.value.length === 0) {
      await loadPackageList();
    }
  } else {
    if (projectListData.value.length === 0) {
      await loadProjectList();
    }
  }
};

// 加载检测项目列表数据
const loadProjectList = async () => {
  try {
    projectListLoading.value = true;
    const res = await ParameterSettings.parameterSettingsListIndex({}, projectFilterParams.value);

    if (res && res.status === 200 && res.success && res.response && res.response.data) {
      projectListData.value = Array.isArray(res.response.data) ? res.response.data : [];
      // 从接口返回中提取model数据
      if (res.response.model && typeof res.response.model === 'object') {
        projectTableModel.value = res.response.model;
      } else {
        projectTableModel.value = undefined;
      }
    } else {
      projectListData.value = [];
      projectTableModel.value = undefined;
    }
  } catch (error) {
    console.error('加载检测项目列表失败:', error);
    projectListData.value = [];
    ElMessage.error('加载检测项目列表失败');
  } finally {
    projectListLoading.value = false;
  }
};
// 处理检测项目筛选
const handleProjectFilter = async (filters: any[]) => {
  // 保存当前的临时选择状态
  const currentSelection = [...tempSelectedProjectIds.value];
  projectFilterParams.value = filters;
  await loadProjectList();
  // 确保选择状态在数据加载后得到恢复
  await nextTick();
  tempSelectedProjectIds.value = currentSelection;
};
// 处理检测项目选择变化（弹框内）
const handleProjectSelectionChange = (selection: any[]) => {
  // 当前页已选项按勾选先后追加；其他页已选项保持原有顺序。
  const currentPageIds = new Set(projectListData.value
    .map((item) => getProjectItemId(item))
    .filter((id) => id !== undefined && id !== null)
    .map(getProjectIdKey));
  const selectedIds = selection
    .map((item) => getProjectItemId(item))
    .filter((id) => id !== undefined && id !== null);
  const selectedIdKeys = new Set(selectedIds.map(getProjectIdKey));
  const nextIds = tempSelectedProjectIds.value.filter((id) => {
    const key = getProjectIdKey(id);
    return !currentPageIds.has(key) || selectedIdKeys.has(key);
  });
  const existingIdKeys = new Set(nextIds.map(getProjectIdKey));

  selectedIds.forEach((id) => {
    const key = getProjectIdKey(id);
    if (!existingIdKeys.has(key)) {
      nextIds.push(id);
      existingIdKeys.add(key);
    }
  });

  tempSelectedProjectIds.value = nextIds;
};

// 加载检测项目包列表数据
const loadPackageList = async () => {
  try {
    packageListLoading.value = true;
    const res = await ParameterSettings.detectionPackageListIndex({}, packageFilterParams.value);

    if (res && res.status === 200 && res.success && res.response && res.response.data) {
      packageListData.value = Array.isArray(res.response.data) ? res.response.data : [];
      // 从接口返回中提取model数据
      if (res.response.model && typeof res.response.model === 'object') {
        packageTableModel.value = res.response.model;
      } else {
        packageTableModel.value = undefined;
      }
    } else {
      packageListData.value = [];
      packageTableModel.value = undefined;
    }
  } catch (error) {
    console.error('加载检测项目包列表失败:', error);
    packageListData.value = [];
    ElMessage.error('加载检测项目包列表失败');
  } finally {
    packageListLoading.value = false;
  }
};

// 处理检测项目包筛选
const handlePackageFilter = async (filters: any[]) => {
  // 保存当前的临时选择状态
  const currentSelection = [...tempSelectedPackageIds.value];
  packageFilterParams.value = filters;
  await loadPackageList();
  // 确保选择状态在数据加载后得到恢复
  await nextTick();
  tempSelectedPackageIds.value = currentSelection;
};

// 处理检测项目包选择变化（弹框内）
const handlePackageSelectionChange = (selection: any[]) => {
  // 只更新临时选择，不影响外层标签显示
  tempSelectedPackageIds.value = selection.map(item => item.Id || item.id);
};

// 查看检测项目包详情
const handlePackageDetail = async (row: any) => {
  try {
    packageDetailLoading.value = true;
    currentPackageDetail.value = row;
    packageDetailVisible.value = true;

    // 调用接口获取项目包包含的检测项目列表
    const packageId = row.Id || row.id;
    const res: any = await ParameterSettings.detectionPackageById({ id: packageId });

    if (res && res.status === 200 && res.success && res.response) {
      // 解析检测项目列表 - 优先使用 检测项目集合
      if (res.response.检测项目集合 && Array.isArray(res.response.检测项目集合)) {
        packageDetailProjects.value = res.response.检测项目集合;
      } else if (res.response.检测项目列表 && Array.isArray(res.response.检测项目列表)) {
        packageDetailProjects.value = res.response.检测项目列表;
      } else if (res.response.projectList && Array.isArray(res.response.projectList)) {
        packageDetailProjects.value = res.response.projectList;
      } else {
        packageDetailProjects.value = [];
      }

      // 提取model数据
      if (res.response.model && typeof res.response.model === 'object') {
        packageDetailProjectsModel.value = res.response.model;
      } else {
        packageDetailProjectsModel.value = undefined;
      }
    } else {
      packageDetailProjects.value = [];
      ElMessage.warning('未能获取项目包详情');
    }
  } catch (error) {
    console.error('加载项目包详情失败:', error);
    packageDetailProjects.value = [];
    ElMessage.error('加载项目包详情失败');
  } finally {
    packageDetailLoading.value = false;
  }
};

// Tab切换处理
const handleTabChange = async (tabName: string) => {
  if (tabName === 'package' && packageListData.value.length === 0) {
    await loadPackageList();
  } else if (tabName === 'project' && projectListData.value.length === 0) {
    await loadProjectList();
  }
};
// 关闭弹窗
const closeProjectDialog = (reset = false) => {
  dialogVisible.value = false;
  // 始终重置筛选条件、排序和数据列表，确保下次打开弹窗时显示完整列表
  projectFilterParams.value = [];
  projectListData.value = [];
  packageFilterParams.value = [];
  packageListData.value = [];

  if (reset) {
    // 重置选中项（仅在取消时）
    selectedProjectIds.value = [];
    selectedPackageIds.value = [];
  }
};
// 确认选择检测项目
const confirmProjectSelection = async () => {
  try {
    // 1. 先处理选中的项目包，获取包内的检测项目
    const projectIdsFromPackages: any[] = [];
    const projectsDataFromPackages: any[] = [];

    if (tempSelectedPackageIds.value.length > 0) {
      // 遍历每个选中的项目包，获取其包含的检测项目
      for (const packageId of tempSelectedPackageIds.value) {
        try {
          const res = await ParameterSettings.detectionPackageById({ id: packageId });
          if (res && res.status === 200 && res.success && res.response) {
            const projects = res.response.检测项目集合 || res.response.检测项目列表 || res.response.projectList || [];
            projects.forEach((project: any) => {
              const projectId = project.Id || project.id;
              // 去重：只添加不在列表中的项目
              if (projectId && !projectIdsFromPackages.includes(projectId)) {
                projectIdsFromPackages.push(projectId);
                projectsDataFromPackages.push(project);
              }
            });
          }
        } catch (error) {
          console.error(`获取项目包 ${packageId} 详情失败:`, error);
        }
      }
    }

    // 2. 合并直接选择的项目和从项目包展开的项目，保留直接勾选的先后顺序。
    const allProjectIds: any[] = [];
    const allProjectIdKeys = new Set<string>();
    const addProjectId = (id: any) => {
      if (id === undefined || id === null) return;
      const key = getProjectIdKey(id);
      if (!allProjectIdKeys.has(key)) {
        allProjectIds.push(id);
        allProjectIdKeys.add(key);
      }
    };
    tempSelectedProjectIds.value.forEach(addProjectId);
    projectIdsFromPackages.forEach(addProjectId);

    const projectsById = new Map<string, any>();
    [...selectedProjectsFullData.value, ...projectListData.value, ...projectsDataFromPackages].forEach((project) => {
      const projectId = getProjectItemId(project);
      if (projectId !== undefined && projectId !== null) {
        projectsById.set(getProjectIdKey(projectId), project);
      }
    });
    const allProjectsData = allProjectIds
      .map((id) => projectsById.get(getProjectIdKey(id)))
      .filter(Boolean);

    // 3. 保存最终结果（只保存项目，不保存项目包）
    selectedProjectIds.value = allProjectIds;
    selectedProjectsFullData.value = allProjectsData;
    selectedPackageIds.value = []; // 清空项目包ID，因为已经展开为项目了
    selectedPackagesFullData.value = [];

    ElMessage.success(`选择完成，共 ${selectedProjectIds.value.length} 个原始记录`);
    closeProjectDialog(false);
  } catch (error) {
    console.error('确认选择失败:', error);
    ElMessage.error('选择失败，请重试');
  }
};

// 移除选中的检测项目
const removeSelectedProject = (projectId: any) => {
  const projectIdKey = getProjectIdKey(projectId);
  const index = selectedProjectIds.value.findIndex((id) => getProjectIdKey(id) === projectIdKey);
  if (index > -1) {
    selectedProjectIds.value.splice(index, 1);
    selectedProjectsFullData.value = selectedProjectsFullData.value.filter((project) => {
      return getProjectIdKey(getProjectItemId(project)) !== projectIdKey;
    });
    ElMessage.success('已移除该原始记录');
  }
};

// 生成带地下层的完整楼层号（升序），如地下 2 层 + 地上 3 层 => [-2, -1, 1, 2, 3]
const getBuildingFloorNumbers = (floors: number, basementFloors: number): number[] => {
  const above = Math.max(0, Math.floor(Number(floors) || 0));
  const below = Math.max(0, Math.floor(Number(basementFloors) || 0));
  const numbers: number[] = [];
  for (let i = below; i >= 1; i--) numbers.push(-i);
  for (let i = 1; i <= above; i++) numbers.push(i);
  return numbers;
};

// 从图纸文件名中解析楼层号，如 -2.jpg、-1.jpg、1.jpg、2.pdf => -2 / -1 / 1 / 2
const parseDrawingFloorFromName = (fileName: string): number | null => {
  const base = String(fileName || '').trim();
  const match = /^(-?\d+)/.exec(base);
  if (!match) return null;
  return parseInt(match[1], 10);
};

const ensureFloorDocumentsStructure = (floors: number, existing: FloorDocument[] = [], basementFloors = 0) => {
  // 创建一个映射来存储现有的楼层文档
  const existingMap = new Map<number, FloorDocument>();
  existing.forEach(doc => {
    existingMap.set(doc.floorNumber, {
      floorNumber: doc.floorNumber,
      files: Array.isArray(doc.files) ? doc.files : []
    });
  });

  // 按“地下层在前、地上层在后”的升序为每一层创建或保留文档
  const documents: FloorDocument[] = [];
  for (const floorNumber of getBuildingFloorNumbers(floors, basementFloors)) {
    if (existingMap.has(floorNumber)) {
      // 如果该楼层已存在文档，则保留它
      documents.push(existingMap.get(floorNumber)!);
    } else {
      // 否则创建一个新的空文档
      documents.push({
        floorNumber,
        files: []
      });
    }
  }
  return documents;
};

const initializeBuildingSelection = (form: FormData | null) => {
  if (!form) {
    selectedBuildingIndex.value = null;
    selectedFloorIndex.value = null;
    return;
  }

  if (!Array.isArray(form.buildings)) {
    form.buildings = [];
  }

  let selectedIndex = form.buildings.findIndex(b => b.selected);
  if (selectedIndex === -1 && form.buildings.length > 0) {
    selectedIndex = 0;
    form.buildings[0].selected = true;
  }

  selectedBuildingIndex.value = selectedIndex >= 0 ? selectedIndex : null;

  if (selectedBuildingIndex.value !== null) {
    const building = form.buildings[selectedBuildingIndex.value];
    building.floorDocuments = ensureFloorDocumentsStructure(building.floors, building.floorDocuments, building.basementFloors);
    selectedFloorIndex.value = building.floorDocuments.length > 0 ? 0 : null;
  } else {
    selectedFloorIndex.value = null;
  }
};

// 辅助函数：查找表单列表中的项索引
const findFormIndex = (row: any): number => {
  return formList.value.findIndex((item: any) => {
    if (row.Id !== undefined && item.Id !== undefined) return item.Id === row.Id;
    if (row.id !== undefined && item.id !== undefined) return item.id === row.id;
    return item === row;
  });
};

// 左侧列表数据（直接使用接口返回的数据）
const formListData = computed(() => {
  return formList.value;
});

const activeFormRowKey = computed(() => {
  if (activeFormIndex.value < 0 || activeFormIndex.value >= formList.value.length) return null;
  const item = formList.value[activeFormIndex.value];
  return item?.Id ?? item?.id ?? null;
});

const findFormItem = (targetId?: number | string, targetName?: string): any => {
  if (targetId) {
    const targetIdText = String(targetId);
    const foundById = formList.value.find((item: any) => String(item.Id ?? item.id ?? '') === targetIdText);
    if (foundById) return foundById;
  }
  if (targetName) {
    return formList.value.find((item: any) => item.名称 === targetName || item.name === targetName);
  }
  return null;
};

// 处理查看按钮点击（设置为查看模式）
const handleFormEdit = async (row: any) => {
  // 每次点击查看都重新加载数据，确保获取最新的数据
  try {
    loading.value = true;
    await handleFormSelect(row);
  } finally {
    loading.value = false;
  }
  isEditMode.value = false; // 设置为查看模式，不可编辑
  ElMessage.success('已进入查看模式');
};

// 进入编辑模式
const enterEditMode = () => {
  if (!canEnterEditMode.value) {
    ElMessage.warning('当前记录不允许编辑');
    return;
  }
  isEditMode.value = true;
  ElMessage.success('已进入编辑模式');
};

// 退出编辑模式
const exitEditMode = () => {
  ElMessageBox.confirm('确定要取消编辑吗？未保存的修改将丢失', '提示', {
    confirmButtonText: '确定',
    cancelButtonText: '取消',
    type: 'warning',
  }).then(() => {
    isEditMode.value = false;
    // 重新加载当前表单数据
    const currentItem = formList.value[activeFormIndex.value];
    if (currentItem) {
      const itemId = currentItem.Id || currentItem.id;
      if (itemId) {
        handleFormSelect(currentItem);
      }
    }
    ElMessage.success('已退出编辑模式');
  }).catch(() => { /* cancelled */ });
};

// 处理删除按钮点击
const handleFormDelete = (row: any) => {
  const index = findFormIndex(row);
  if (index >= 0) deleteForm(index);
};

// 加载表单列表数据（仅用于左侧列表展示）
const loadFormList = async () => {
  try {
    listLoading.value = true;
    // 构建分页参数
    const paginationData = {
      page: formPagination.value.page,
      intPageSize: formPagination.value.pageSize,
      key: quickKeyword.value.trim()
    };
    const res = await ParameterSettings.operatingProjectListIndex(paginationData, filterParams.value);

    if (res && res.status === 200 && res.success && res.response && res.response.data) {
      formList.value = Array.isArray(res.response.data) ? res.response.data : [];
      // 更新总数 - 使用dataCount字段
      formPagination.value.total = res.response.dataCount || res.response.total || res.response.count || 0;
      // 从接口返回中提取model数据
      if (res.response.model && typeof res.response.model === 'object') {
        tableModel.value = res.response.model;
      } else {
        tableModel.value = undefined;
      }
    } else {
      formList.value = [];
      tableModel.value = undefined;
      formPagination.value.total = 0;
    }
  } catch (error) {
    console.error('加载表单列表失败:', error);
    formList.value = [];
  } finally {
    listLoading.value = false;
  }
};

const handleQuickQuery = () => {
  formPagination.value.page = 1;
  loadFormList();
};

const handleQuickSync = async () => {
  try {
    quickSyncLoading.value = true;
    const res = await ParameterSettings.operatingProjectQuickSync({
      key: quickKeyword.value.trim()
    });

    if (res && res.status === 200 && res.success) {
      ElMessage.success(res.msg || '快速同步成功');
      formPagination.value.page = 1;
      await loadFormList();
    } else {
      ElMessage.error(res?.msg || '快速同步失败');
    }
  } catch (error) {
    console.error('快速同步失败:', error);
    ElMessage.error('快速同步失败');
  } finally {
    quickSyncLoading.value = false;
  }
};

// 筛选处理函数
const handleFilter = (filters: any[]) => {
  filterParams.value = filters;
  formPagination.value.page = 1; // 筛选时重置到第一页
  loadFormList(); // 重新加载数据
};

// 分页器事件处理
const handleFormPageChange = (page: number) => {
  formPagination.value.page = page;
  loadFormList();
};

const handleFormPageSizeChange = (pageSize: number) => {
  formPagination.value.pageSize = pageSize;
  formPagination.value.page = 1; // 改变页大小时重置到第一页
  loadFormList();
};

// 解析JSON字段：优先使用对象，退化为JSON字符串解析
const parseJsonField = (objField: unknown, jsonField: unknown): Record<string, string> => {
  // 优先使用对象字段
  if (objField && typeof objField === 'object' && Object.keys(objField as Record<string, unknown>).length > 0) {
    return objField as Record<string, string>;
  }
  // 退化为JSON字符串解析
  if (jsonField && typeof jsonField === 'string' && jsonField !== '{}') {
    try {
      const parsed = JSON.parse(jsonField);
      if (parsed && typeof parsed === 'object' && Object.keys(parsed).length > 0) {
        return parsed as Record<string, string>;
      }
    } catch { /* ignore parse error */ }
  }
  return {};
};

const loadFormDetail = async (item: any) => {
  // 这里应该根据实际返回的数据结构进行解析
  const buildings: Building[] = [];

  // 如果有楼栋数据，则解析
  if (item.建筑列表 && Array.isArray(item.建筑列表)) {
    item.建筑列表.forEach((b: any, idx: number) => {
      const floors = parseInt(b.层数) || 1;
      const basementFloors = parseInt(b.地下层数) || 0;
      const floorDocs: FloorDocument[] = [];

      // 修改这里：正确按照楼层号组织图纸数据
      if (Array.isArray(b.图纸包)) {
        // 创建一个映射，将图纸按楼层号分组
        const floorDrawingMap = new Map<number, any[]>();

        b.图纸包.forEach((doc: any) => {
          // 图纸包中的图纸携带“层数”字段时以其为准（可为 -1、-2 等地下层）
          const drawingFloor = Number(doc?.层数);
          // 没有层数标记时按图纸包原有顺序对应楼层（兼容旧数据：顺序即地上楼层）
          const floorIndex = !Number.isNaN(drawingFloor)
            ? drawingFloor
            : b.图纸包.indexOf(doc) + 1; // 从1开始计数

          if (!floorDrawingMap.has(floorIndex)) {
            floorDrawingMap.set(floorIndex, []);
          }

          // 现在后端返回内容为文件编码/文件名，保存为 serverCode
          if (doc.内容) {
            const serverCode = String(doc.内容);
            const isPdf = serverCode.toLowerCase().endsWith('.pdf');
            const fileName = isPdf
              ? `${floorIndex}层图纸_${doc.Id || Date.now()}.pdf`
              : `${floorIndex}层图纸_${doc.Id || Date.now()}.jpg`;
            const fileObj = {
              name: fileName,
              uid: genFileId(),
              status: 'success',
              serverCode,
              url: '',
              loading: !isPdf,
              isPdf
            };

            floorDrawingMap.get(floorIndex)?.push(fileObj);
          }
        });

        // 将分组后的文件添加到对应楼层文档中
        floorDrawingMap.forEach((files, floorNumber) => {
          floorDocs.push({
            floorNumber: floorNumber,
            files: files
          });
        });
      }

      buildings.push({
        id: b.Id || Date.now().toString() + Math.random(),
        code: b.报告编号 || b.编码 || '',
        name: b.名称 || '',
        floors,
        basementFloors,
        selected: idx === 0,
        floorDocuments: ensureFloorDocumentsStructure(floors, floorDocs, basementFloors),
        editing: false
      });
    });
  }

  // 获取建筑个数，优先使用接口返回的，否则使用建筑数组长度
  const buildingCount = item.buildingCount || item.建筑个数 || item.建筑列表?.length || 0;

  // 如果没有建筑数据但有建筑个数，自动生成建筑
  if (buildings.length === 0 && buildingCount > 0) {
    for (let i = 1; i <= buildingCount; i++) {
      buildings.push({
        id: Date.now().toString() + i,
        code: '',
        name: `${i}号楼`,
        floors: 1,
        basementFloors: 0,
        selected: i === 1,
        floorDocuments: ensureFloorDocumentsStructure(1),
        editing: false
      });
    }
  }

  // 处理检测项目列表
  const projectIds: any[] = [];
  const projectsData: any[] = [];
  if (item.检测项目列表 && Array.isArray(item.检测项目列表)) {
    item.检测项目列表.forEach((project: any) => {
      const projectId = project.Id || project.id;
      if (projectId) {
        projectIds.push(projectId);
        // 保存项目的完整数据，用于查看模式显示
        projectsData.push(project);
      }
    });
  }

  return {
    id: item.id || item.Id,
    code: item.code || item.编码 || '',
    name: item.name || item.名称 || '',
    projectManagerCode: item.projectManagerCode || item.项目负责人code || item.项目负责人Code || item.项目负责人codejson || '',
    projectManager: item.projectManager || item.项目负责人 || '',
    responsibleDepartment: item.responsibleDepartment || item.主责部门 || '',
    status: item.status || item.状态 || '',
    startTime: item.startTime || item.项目开始时间 || null,
    remark: item.remark || item.说明 || '',
    buildingCount: buildingCount,
    buildings: buildings,
    projectIds: projectIds,
    projectsData: projectsData,
    // 解析项目助理：接口返回为对象或JSON字符串
    projectAssistant: parseJsonField(item.项目助理, item.项目助理codejson),
    // 解析执行团队：接口返回为对象或JSON字符串
    executionTeam: parseJsonField(item.执行团队, item.执行团队codejson)
  };
};

// 组件挂载后加载数据
onMounted(() => {
  loadFormList();
  // 设置左侧面板默认宽度为容器的一半
  nextTick(() => {
    if (mainContentRef.value) {
      leftPanelWidth.value = mainContentRef.value.getBoundingClientRect().width / 2;
    }
  });
});

// 添加新表单
const addNewForm = () => {
  const formName = `表单${formList.value.length + 1}`;
  const newForm = createEmptyForm();
  newForm.name = formName;

  // 创建列表项数据结构（用于左侧列表显示）
  const newListItem: any = {
    名称: formName,
    name: formName,
    编码: `FD${String(formList.value.length + 1).padStart(4, '0')}`,
    _isNew: true,
    _formData: newForm
  };

  formList.value.push(newListItem);
  activeFormIndex.value = formList.value.length - 1;
  currentForm.value = newForm;
  currentFormEditable.value = true;
  selectedProjectIds.value = [];
  resetProjectFiles();
  isEditMode.value = true;

  ElMessage.success('新增表单成功');
};

// 刷新当前表单的所有缩略图
const refreshThumbnails = async () => {
  if (!currentForm.value || !currentForm.value.buildings) return;

  const tasks: Promise<void>[] = [];

  currentForm.value.buildings.forEach(building => {
    if (building.floorDocuments) {
      building.floorDocuments.forEach(doc => {
        if (doc.files) {
          doc.files.forEach((file: any) => {
            if (file.serverCode && !file.url && !file.pdfUrl) {
              // 已确认的图片可直接使用缓存；PDF 和未知类型文件需下载后重新识别。
              if (!file.isPdf && thumbnailCache.has(file.serverCode)) {
                file.url = thumbnailCache.get(file.serverCode)!;
                file.loading = false;
              } else {
                file.loading = true;
                tasks.push(
                  loadFloorDocumentPreview(file).catch((error) => {
                    console.warn(`图纸缩略图加载失败 (${file.serverCode}):`, error);
                  }).finally(() => {
                    file.loading = false;
                    const fileIndex = doc.files.indexOf(file);
                    if (fileIndex !== -1) {
                      doc.files.splice(fileIndex, 1, { ...file });
                    }
                  })
                );
              }
            } else {
              // 如果已经有url或者不需要加载，确保loading为false
              file.loading = false;
            }
          });
        }
      });
    }
  });

  if (tasks.length > 0) {
    await Promise.all(tasks);
  }
};

// 选择表单
const handleFormSelect = async (row: any) => {
  const index = findFormIndex(row);

  if (index >= 0) {
    resetProjectFiles();
    activeFormIndex.value = index;
    const item = formList.value[index];

    // 如果是新添加的表单且有_formData，直接使用
    if (item._isNew && item._formData) {
      currentForm.value = item._formData;
      currentFormEditable.value = true;
      initializeBuildingSelection(currentForm.value);
      return;
    }

    // 获取ID（支持 Id 或 id 字段）
    const itemId = item.Id || item.id;

    // 如果有ID，调用详情接口加载数据
    if (itemId) {
      try {
        loading.value = true;
        // 调用详情接口
        const res = await ParameterSettings.operatingProjectById({ id: itemId });

        if (res && res.status === 200 && res.success && res.response) {
          currentFormEditable.value = toEditableFlag((res as any).editable,
            toEditableFlag((res as any).response?.editable, false));
          // 解析详情数据
          const formData = await loadFormDetail(res.response);
          if (!formData.id) {
            formData.id = itemId;
          }
          currentForm.value = formData;
          // 设置已选中的检测项目ID和完整数据
          if (formData.projectIds && Array.isArray(formData.projectIds)) {
            selectedProjectIds.value = [...formData.projectIds];
          } else {
            selectedProjectIds.value = [];
          }
          if (formData.projectsData && Array.isArray(formData.projectsData)) {
            selectedProjectsFullData.value = [...formData.projectsData];
          } else {
            selectedProjectsFullData.value = [];
          }
          initializeBuildingSelection(currentForm.value);
          // 刷新缩略图，使用 setTimeout 确保在全局 loading 消失后才开始加载图片，避免阻塞界面交互
          setTimeout(() => {
            refreshThumbnails();
          }, 50);
          loadProjectFiles(String(itemId), false);
          ElMessage.success('数据加载成功');
        } else {
          ElMessage.error('数据加载失败');
          currentFormEditable.value = false;
          currentForm.value = createEmptyForm();
          selectedProjectIds.value = [];
          initializeBuildingSelection(currentForm.value);
        }
      } catch (error) {
        console.error('加载表单详情失败:', error);
        ElMessage.error('数据加载失败');
        currentFormEditable.value = false;
        currentForm.value = createEmptyForm();
        selectedProjectIds.value = [];
        initializeBuildingSelection(currentForm.value);
      } finally {
        loading.value = false;
      }
    } else {
      // 如果没有ID（可能是新增的表单），检查是否已有完整数据
      currentFormEditable.value = toEditableFlag(item.editable, true);
      if (item.buildings) {
        // 如果已有完整数据，直接使用
        currentForm.value = item as FormData;
      } else {
        currentForm.value = createEmptyForm();
      }
      selectedProjectIds.value = [];
      initializeBuildingSelection(currentForm.value);
    }
  }
};

// 删除表单
const deleteForm = async (index: number) => {
  const item = formList.value[index];
  if (!item) return;

  const itemId = item.Id || item.id;

  // 如果是新建的表单（没有ID），直接从前端删除
  if (!itemId || item._isNew) {
    ElMessageBox.confirm('确定要删除这个表单吗？', '提示', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning',
    }).then(() => {
      formList.value.splice(index, 1);
      if (activeFormIndex.value === index) {
        activeFormIndex.value = -1;
        currentForm.value = null;
        currentFormEditable.value = false;
      }
      ElMessage.success('删除成功');
    }).catch(() => { /* cancelled */ });
    return;
  }

  // 有ID的情况，调用后端API删除
  ElMessageBox.confirm('确定要删除这个表单吗？删除后无法恢复', '提示', {
    confirmButtonText: '确定',
    cancelButtonText: '取消',
    type: 'warning',
  }).then(async () => {
    try {
      // 调用删除接口
      const res = await ParameterSettings.operatingProjectDelete({ id: itemId });

      if (res && res.status === 200 && res.success) {
        const isDeletingActive = activeFormIndex.value === index;
        const activeItem = activeFormIndex.value >= 0 && activeFormIndex.value < formList.value.length
          ? formList.value[activeFormIndex.value]
          : null;
        const activeItemId = activeItem?.Id ?? activeItem?.id;

        // 如果删除的是当前选中的表单，清空选中状态
        if (isDeletingActive) {
          activeFormIndex.value = -1;
          currentForm.value = null;
          currentFormEditable.value = false;
        } else if (activeFormIndex.value > index) {
          // 如果删除的项在当前选中项之前，需要调整索引
          activeFormIndex.value--;
        }

        if (formList.value.length === 1 && formPagination.value.page > 1) {
          formPagination.value.page--;
        }
        await loadFormList();
        if (!isDeletingActive && activeItemId) {
          const foundItem = findFormItem(activeItemId);
          if (foundItem) {
            activeFormIndex.value = findFormIndex(foundItem);
          } else {
            activeFormIndex.value = -1;
            currentForm.value = null;
            currentFormEditable.value = false;
          }
        }
        ElMessage.success('删除成功');
      } else {
        ElMessage.error('删除失败');
      }
    } catch (error) {
      console.error('删除表单失败:', error);
      ElMessage.error('删除失败');
    }
  }).catch(() => { /* cancelled */ });
};

// 生成楼栋按钮
const generateBuildings = () => {
  if (!currentForm.value) return;

  const count = currentForm.value.buildingCount;
  if (count <= 0) {
    ElMessage.warning('建筑个数必须大于0');
    return;
  }

  if (count > 20) {
    ElMessage.warning('建筑个数不能超过20');
    return;
  }

  const currentBuildings = currentForm.value.buildings || [];
  const currentCount = currentBuildings.length;

  // 如果新数量小于当前数量，需要删除多余的建筑
  if (count < currentCount) {
    currentForm.value.buildings = currentBuildings.slice(0, count);
    // 如果删除的是当前选中的建筑，重置选择
    if (selectedBuildingIndex.value !== null && selectedBuildingIndex.value >= count) {
      selectedBuildingIndex.value = 0;
      if (currentForm.value.buildings.length > 0) {
        currentForm.value.buildings.forEach((b, idx) => {
          b.selected = idx === 0;
        });
      }
    }
  }
  // 如果新数量大于当前数量，添加新的建筑
  else if (count > currentCount) {
    for (let i = currentCount + 1; i <= count; i++) {
      currentForm.value.buildings.push({
        id: Date.now().toString() + i,
        code: '',
        name: `${i}号楼`,
        floors: 1,
        basementFloors: 0,
        selected: false,
        floorDocuments: ensureFloorDocumentsStructure(1),
        editing: false
      });
    }
  }

  initializeBuildingSelection(currentForm.value);

  ElMessage.success(`成功生成${count}栋楼`);
};

// 选择楼栋
const currentFloorDocuments = computed<FloorDocument[]>(() => {
  if (!currentForm.value || selectedBuildingIndex.value === null) return [];
  const building = currentForm.value.buildings[selectedBuildingIndex.value];
  building.floorDocuments = ensureFloorDocumentsStructure(building.floors, building.floorDocuments, building.basementFloors);
  return building.floorDocuments;
});

const selectedFloorDocument = computed<FloorDocument | null>(() => {
  const documents = currentFloorDocuments.value;
  if (documents.length === 0) return null;
  if (selectedFloorIndex.value === null || selectedFloorIndex.value >= documents.length) {
    return documents[0];
  }
  return documents[selectedFloorIndex.value];
});

const selectBuilding = (index: number) => {
  if (!currentForm.value) return;

  // 取消之前选中项的选中状态
  currentForm.value.buildings.forEach((building, idx) => {
    building.selected = idx === index;
    building.editing = false;
  });

  selectedBuildingIndex.value = index;
  selectedFloorIndex.value = 0;
};

const startBuildingRename = (index: number) => {
  if (!isEditMode.value || !currentForm.value) return;
  currentForm.value.buildings.forEach((building, idx) => {
    building.editing = idx === index;
  });
  nextTick(() => {
    // 保持按钮选中状态不变
    selectBuilding(index);
  });
};

const finishBuildingRename = (index: number) => {
  if (!currentForm.value) return;
  const building = currentForm.value.buildings[index];
  if (!building) return;
  building.name = (building.name || '').trim() || `${index + 1}号楼`;
  building.editing = false;
};

const selectFloor = (index: number) => {
  const documents = currentFloorDocuments.value;
  if (index >= 0 && index < documents.length) {
    selectedFloorIndex.value = index;
  }
};

const handleBuildingFloorChange = (value: number | null) => {
  if (!currentForm.value || selectedBuildingIndex.value === null) return;
  const building = currentForm.value.buildings[selectedBuildingIndex.value];
  const floors = value && value > 0 ? value : 0;
  building.floors = floors;
  building.floorDocuments = ensureFloorDocumentsStructure(floors, building.floorDocuments, building.basementFloors);
  const totalFloors = building.floorDocuments.length;
  if (!floors && !(building.basementFloors || 0)) {
    selectedFloorIndex.value = null;
  } else if (selectedFloorIndex.value === null || selectedFloorIndex.value >= totalFloors) {
    selectedFloorIndex.value = totalFloors > 0 ? totalFloors - 1 : null;
  }
};

// 地下层数变化
const handleBasementFloorChange = (value: number | null) => {
  if (!currentForm.value || selectedBuildingIndex.value === null) return;
  const building = currentForm.value.buildings[selectedBuildingIndex.value];
  const basementFloors = value && value > 0 ? Math.floor(value) : 0;
  building.basementFloors = basementFloors;
  building.floorDocuments = ensureFloorDocumentsStructure(building.floors, building.floorDocuments, basementFloors);
  const totalFloors = building.floorDocuments.length;
  if (!building.floors && !basementFloors) {
    selectedFloorIndex.value = null;
  } else if (selectedFloorIndex.value === null || selectedFloorIndex.value >= totalFloors) {
    selectedFloorIndex.value = totalFloors > 0 ? totalFloors - 1 : null;
  }
};

// 增加/减少地下层数
const incrementBasementFloor = () => {
  if (!currentForm.value || selectedBuildingIndex.value === null) return;
  const building = currentForm.value.buildings[selectedBuildingIndex.value];
  if (building.basementFloors < 50) {
    building.basementFloors++;
    handleBasementFloorChange(building.basementFloors);
  }
};

const decrementBasementFloor = () => {
  if (!currentForm.value || selectedBuildingIndex.value === null) return;
  const building = currentForm.value.buildings[selectedBuildingIndex.value];
  if (building.basementFloors > 0) {
    building.basementFloors--;
    handleBasementFloorChange(building.basementFloors);
  }
};

// 增加楼层
const incrementFloor = () => {
  if (!currentForm.value || selectedBuildingIndex.value === null) return;
  const building = currentForm.value.buildings[selectedBuildingIndex.value];
  if (building.floors < 100) {
    building.floors++;
    handleBuildingFloorChange(building.floors);
  }
};

// 减少楼层
const decrementFloor = () => {
  if (!currentForm.value || selectedBuildingIndex.value === null) return;
  const building = currentForm.value.buildings[selectedBuildingIndex.value];
  if (building.floors > 1) {
    building.floors--;
    handleBuildingFloorChange(building.floors);
  }
};

// 重置表单
const resetForm = () => {
  if (formRef.value) {
    formRef.value.resetFields();
  }
  if (currentForm.value) {
    currentForm.value.buildings = [];
    selectedBuildingIndex.value = null;
    selectedFloorIndex.value = null;
  }
};

const handleFloorFileChange: UploadProps['onChange'] = async (_file, uploadFiles) => {
  const doc = selectedFloorDocument.value;
  if (!doc) return;
  const first = uploadFiles[0];
  if (!first || !first.raw) return;

  if (!isSupportedDrawingFile(first as any)) {
    ElMessage.warning('仅支持图片和 PDF 格式的图纸');
    return;
  }

  const isPdf = isPdfFile(first);

  floorUploading.value = true; // 开始上传
  floorUploadProgress.value = 0; // 重置进度
  try {
    const form = new FormData();
    // 上传接口统一使用 image 字段，PDF 也必须先取得服务器文件编码后才能随项目保存。
    form.append('image', first.raw as any, (first as any).name || (isPdf ? 'upload.pdf' : 'upload.jpg'));
    const res: any = await ParameterSettings.operatingProjectUploadWithProgress(form, (progress: number) => {
      floorUploadProgress.value = progress;
    });
    if (res && res.success) {
      const code = res.response || res.data;
      const uploaded: any = {
        name: first.name,
        uid: genFileId(),
        status: 'success',
        serverCode: code,
        url: '',
        loading: true,
        isPdf
      };

      if (isPdf) {
        uploaded.raw = first.raw;
        uploaded.pdfUrl = URL.createObjectURL(first.raw);
        uploaded.thumbnailUrl = '';
      }

      // 每层只保留一张
      doc.files.splice(0, doc.files.length, uploaded);

      if (isPdf) {
        generatePdfThumbnail(first.raw).then(thumbnailUrl => {
          uploaded.thumbnailUrl = thumbnailUrl;
        }).catch((error) => {
          console.warn('生成 PDF 缩略图失败:', error);
        }).finally(() => {
          uploaded.loading = false;
          if (doc.files.length > 0) doc.files.splice(0, 1, { ...uploaded });
        });
      } else {
        fetchImageContent(code).then(url => {
          if (url) uploaded.url = url;
          uploaded.loading = false;
          if (doc.files.length > 0) doc.files.splice(0, 1, { ...uploaded });
        }).catch((err) => {
          console.error('获取缩略图失败:', err);
          uploaded.loading = false;
          if (doc.files.length > 0) doc.files.splice(0, 1, { ...uploaded });
        });
      }

      ElMessage.success(isPdf ? 'PDF 上传成功' : '上传成功');
    } else {
      ElMessage.error('上传失败');
    }
  } catch (e) {
    console.error('上传失败', e);
    ElMessage.error('上传失败');
  } finally {
    floorUploading.value = false; // 上传结束
    floorUploadProgress.value = 0; // 重置进度
  }
};

// 处理超出文件数量限制
const handleExceed: UploadProps['onExceed'] = (files, uploadFiles) => {
  ElMessage.warning('每层只能上传一张图纸，如需更换请先删除当前图纸');
};

const handleFloorFileRemove: UploadProps['onRemove'] = () => {
  // 使用自定义删除按钮处理，onRemove不做同步
};

// 自定义删除文件函数（同时请求后端删除）
const removeFloorFile = async (fileIndex: number) => {
  const doc = selectedFloorDocument.value;
  if (!doc || fileIndex < 0 || fileIndex >= doc.files.length) return;
  const target: any = doc.files[fileIndex];
  try {
    await ElMessageBox.confirm('确定要删除这个图纸吗？', '提示', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning',
    });
    if (target?.serverCode) {
      try {
        await ParameterSettings.operatingProjectDeleteImage({ fileName: target.serverCode });
      } catch (e) {
        console.warn('后端删除失败或已不存在，继续本地移除', e);
      }
    }
    doc.files.splice(fileIndex, 1);
    ElMessage.success('删除成功');
  } catch {
    // 用户取消
  }
};

// 批量上传文件夹功能
const batchUpload = () => {
  if (!currentForm.value || selectedBuildingIndex.value === null) {
    ElMessage.warning('请先选择楼栋');
    return;
  }

  const input = document.createElement('input');
  input.type = 'file';
  input.webkitdirectory = true;
  input.multiple = true;
  input.accept = 'image/*,.pdf,application/pdf';

  input.onchange = async (event: any) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    // 将文件按名称排序
    const selectedFiles = Array.from(files) as File[];
    const ignoredCount = selectedFiles.filter(file => !isSupportedDrawingFile(file)).length;
    const fileArray = selectedFiles.filter(file => isSupportedDrawingFile(file)).sort((a, b) => {
      const nameA = a.name.toLowerCase();
      const nameB = b.name.toLowerCase();
      return nameA.localeCompare(nameB);
    });

    if (fileArray.length === 0) {
      ElMessage.warning('文件夹中没有可上传的图片或 PDF 图纸');
      return;
    }
    if (ignoredCount > 0) {
      ElMessage.warning(`已忽略 ${ignoredCount} 个非图片或 PDF 文件`);
    }

    const building = currentForm.value!.buildings[selectedBuildingIndex.value!];
    // 确保楼层文档覆盖“地下层+地上层”（升序：地下在前，地上在后）
    building.floorDocuments = ensureFloorDocumentsStructure(building.floors, building.floorDocuments, building.basementFloors);
    const floorDocs = building.floorDocuments;
    const allFloorNumbers = floorDocs.map(doc => doc.floorNumber);
    const assignedFloorNumbers = new Set<number>();
    let uploadedCount = 0;

    // 从文件名解析所属楼层（如 -2.jpg、-1.jpg、1.jpg），无法识别时按顺序补位
    const resolveTargetDoc = (file: File): FloorDocument | null => {
      const parsedFloor = parseDrawingFloorFromName(file.name);
      if (parsedFloor !== null && allFloorNumbers.includes(parsedFloor) && !assignedFloorNumbers.has(parsedFloor)) {
        const matched = floorDocs.find(doc => doc.floorNumber === parsedFloor);
        if (matched) return matched;
      }
      // 未识别或楼层已占用：优先补位到尚未分配图纸的地上层，其次地下层
      const freeDocs = floorDocs.filter(doc => !assignedFloorNumbers.has(doc.floorNumber));
      const aboveGround = freeDocs.find(doc => doc.floorNumber > 0);
      return aboveGround || freeDocs[0] || null;
    };

    // 分配文件到各楼层并上传，每层只保留一张图纸
    for (const file of fileArray) {
      const targetDoc = resolveTargetDoc(file);
      if (!targetDoc) break;
      const targetFloorNumber = targetDoc.floorNumber;
      const isPdf = isPdfFile(file as any);

      try {
        const form = new FormData();
        // 按参考实现，使用 image 字段并携带文件名
        form.append('image', file, file.name || (isPdf ? 'upload.pdf' : 'upload.jpg'));
        const res: any = await ParameterSettings.operatingProjectUpload(form);
        if (res && res.success) {
          const code = res.response || res.data;
          const uploaded: any = {
            name: file.name,
            uid: genFileId(),
            status: 'success',
            serverCode: code,
            url: '',
            loading: true,
            isPdf
          };

          if (isPdf) {
            uploaded.raw = file;
            uploaded.pdfUrl = URL.createObjectURL(file);
            uploaded.thumbnailUrl = '';
          }

          // 每层只保留一张图纸
          targetDoc.files = [uploaded];
          assignedFloorNumbers.add(targetFloorNumber);
          uploadedCount += 1;

          const finishPreview = () => {
            uploaded.loading = false;
            if (targetDoc.files.length) {
              targetDoc.files.splice(0, 1, { ...uploaded });
            }
          };

          if (isPdf) {
            generatePdfThumbnail(file).then(thumbnailUrl => {
              uploaded.thumbnailUrl = thumbnailUrl;
            }).catch((error) => {
              console.warn('生成 PDF 缩略图失败:', error);
            }).finally(finishPreview);
          } else {
            fetchImageContent(code).then(url => {
              if (url) uploaded.url = url;
            }).finally(finishPreview);
          }
        } else {
          ElMessage.error(`第${targetFloorNumber}层上传失败`);
        }
      } catch (e) {
        console.error('批量上传失败:', e);
        ElMessage.error(`第${targetFloorNumber}层上传失败`);
      }
    }

    // 更新当前选中楼层的文件显示
    if (selectedFloorIndex.value !== null && floorDocs[selectedFloorIndex.value]) {
      selectedFloorDocument.value?.files.splice(0, selectedFloorDocument.value?.files.length, ...floorDocs[selectedFloorIndex.value].files);
    }

    ElMessage.success(`成功批量上传 ${uploadedCount} 个楼层的图纸`);
  };

  input.click();
};

const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(((reader.result as string) || '').split(',')[1] || '');
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

// 保存表单数据
const saveFormData = async () => {
  if (!currentForm.value) {
    ElMessage.warning('请先选择或创建表单');
    return;
  }

  // 表单验证
  if (formRef.value) {
    await formRef.value.validate(async (valid: boolean) => {
      if (valid) {
        const currentItem = activeFormIndex.value >= 0 && activeFormIndex.value < formList.value.length
          ? formList.value[activeFormIndex.value]
          : null;

        const itemId = currentItem?.Id || currentItem?.id;
        const isNew = currentItem?._isNew || !itemId;

        // 校验：楼栋报告编号必填
        if (!currentForm.value) {
          ElMessage.warning('表单数据为空');
          return;
        }
        const buildingsArr = currentForm.value.buildings || [];
        const missingIndex = buildingsArr.findIndex(b => !b.code || !String(b.code).trim());
        if (missingIndex !== -1) {
          selectBuilding(missingIndex);
          ElMessage.warning('请为所有楼栋填写报告编号');
          return;
        }

        let submitStatus = currentForm.value?.status || currentItem?.状态 || currentItem?.status || '';
        if (submitStatus === '等待配置完成') {
          try {
            await ElMessageBox.confirm(
              '当前项目为“等待配置完成”，是否配置完成”？',
              '提示',
              {
                confirmButtonText: '是',
                cancelButtonText: '否',
                type: 'warning',
                distinguishCancelAndClose: true
              }
            );
            submitStatus = '配置完成，执行中';
          } catch {
            // 选择“否”或关闭弹框时，保持原状态继续保存
          }
        }

        // 处理建筑图纸文件，提交服务器返回的文件编码
        const buildingList = await Promise.all((currentForm.value?.buildings || []).map(async (building) => {
          // 处理每个楼层的图纸文件，确保所有楼层都有占位数据（楼层升序：地下在前、地上在后）
          const floorDocs = ensureFloorDocumentsStructure(building.floors, building.floorDocuments, building.basementFloors);
          const drawings = [];

          // 为每一层创建图纸数据（即使没有上传图片也需要占位），并标注该图纸所属层数
          for (const floorDoc of floorDocs) {
            // 查找该楼层是否已有上传的文件
            const uploadedFile = floorDoc && floorDoc.files && floorDoc.files.length > 0 ? floorDoc.files[0] : undefined;
            // 每层只处理第一个文件（确保只有一张图片）
            const f: any = uploadedFile;
            const code = f?.serverCode;
            drawings.push({
              导航ID: '',
              内容: code || '',
              层数: floorDoc.floorNumber,
              ISDELETED: false,
              Id: ''
            });
          }

          return {
            报告编号: building.code,
            名称: building.name,
            层数: building.floors.toString(),
            地下层数: (building.basementFloors || 0) > 0 ? building.basementFloors : null,
            经营项目ID: itemId || '',
            图纸包: drawings,
            ISDELETED: false,
            Id: ''
          };
        })) || [];

        // 准备提交数据 - 构造符合接口要求的数据格式
        const startISO = currentForm.value?.startTime ? new Date(currentForm.value.startTime as any).toISOString() : undefined;
        const projectAssistantObj = currentForm.value?.projectAssistant || {};
        const executionTeamObj = currentForm.value?.executionTeam || {};
        const submitData = {
          编码: currentForm.value?.code,
          状态: submitStatus,
          名称: currentForm.value?.name,
          项目负责人: currentForm.value?.projectManager,
          项目负责人code: currentForm.value?.projectManagerCode,
          主责部门: currentForm.value?.responsibleDepartment,
          项目助理: projectAssistantObj,
          项目助理codejson: JSON.stringify(projectAssistantObj),
          说明: currentForm.value?.remark,
          项目开始时间: startISO,
          ISDELETED: false,
          建筑列表: buildingList,
          检测项目列表: selectedProjectIds.value.map(id => ({ Id: id })),
          执行团队: executionTeamObj,
          执行团队codejson: JSON.stringify(executionTeamObj),
          Id: itemId || ''
        };

        try {
          // 开始保存，显示loading
          saveLoading.value = true;

          // 调用保存接口
          const res = await ParameterSettings.operatingProjectSave(
            submitData

          );

          if (res && res.status === 200 && res.success) {
            if (isNew) {
              ElMessage.success('新增成功');
              // 获取返回的ID（后端返回格式：response.ID）
              const newId = res.response?.ID || res.response?.Id || res.response?.id;
              const targetName = currentForm.value?.name;

              // 新增成功后刷新列表
              formPagination.value.page = 1;
              await loadFormList();

              // 刷新列表后，重新选中当前项（优先通过ID匹配，其次通过名称匹配）
              const foundItem = newId ? findFormItem(newId) : findFormItem(undefined, targetName);
              if (foundItem) {
                await handleFormSelect(foundItem);
              } else if (newId) {
                const detailRes = await ParameterSettings.operatingProjectById({ id: newId });
                if (detailRes && detailRes.status === 200 && detailRes.success && detailRes.response) {
                  currentFormEditable.value = toEditableFlag((detailRes as any).editable,
                    toEditableFlag((detailRes as any).response?.editable, false));
                  const formData = await loadFormDetail(detailRes.response);
                  currentForm.value = formData;
                  selectedProjectIds.value = Array.isArray(formData.projectIds) ? [...formData.projectIds] : [];
                  selectedProjectsFullData.value = Array.isArray(formData.projectsData) ? [...formData.projectsData] : [];
                  initializeBuildingSelection(currentForm.value);
                  activeFormIndex.value = -1;
                }
              }
              // 保存成功后退出编辑模式
              isEditMode.value = false;
            } else {
              ElMessage.success('更新成功');
              if (currentItem) {
                currentItem.状态 = submitStatus;
                currentItem.status = submitStatus;
                currentItem.名称 = currentForm.value?.name;
                currentItem.name = currentForm.value?.name;
                currentItem.编码 = currentForm.value?.code;
              }
              currentForm.value.status = submitStatus;

              // 保存成功后刷新左侧列表，确保列表中的主责部门等字段同步更新
              await loadFormList();

              // 保存成功后重新加载当前表单数据以确保显示最新数据
              if (itemId) {
                await handleFormSelect({ Id: itemId, id: itemId });
              }
              // 保存成功后退出编辑模式
              isEditMode.value = false;
            }
          } else {
            ElMessage.error(isNew ? '新增失败' : '更新失败');
          }
        } catch (error) {
          console.error('保存表单数据失败:', error);
          ElMessage.error(isNew ? '新增失败' : '更新失败');
        } finally {
          // 保存完成，关闭loading
          saveLoading.value = false;
        }
      } else {
        ElMessage.warning('请填写必填项');
      }
    });
  }
};

</script>

<style scoped>
/* 让右侧面板在弹性布局下可以收缩，避免产生横向滚动条 */
.right-panel {
  min-width: 0;
}

/* 表单内容区域占满宽度 */
.form-content {
  width: 100%;
}

.limited-field {
  width: clamp(320px, 42vw, 560px);
  max-width: 100%;
}

.limited-field :deep(.el-input),
.limited-field :deep(.el-date-editor) {
  width: 100%;
}

/* 楼栋个数行在窄屏下自动换行 */
.building-count-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

/* 让建筑个数输入占据剩余空间，按钮靠右 */
.building-count-row :deep(.el-input-number) {
  flex: 1 1 auto;
  width: auto;
  min-width: 0;
}

.building-count-row .el-button {
  margin-left: auto;
}

.project-file-toolbar {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 12px;
}

:deep(.project-file-upload-dialog .el-dialog) {
  width: min(680px, calc(100vw - 32px)) !important;
  overflow: hidden;
  border-radius: 10px;
  box-shadow: 0 18px 44px rgba(31, 45, 61, 0.22);
}

:deep(.project-file-upload-dialog .el-dialog__header) {
  margin-right: 0;
  padding: 20px 24px 16px;
  border-bottom: 1px solid #edf0f5;
}

:deep(.project-file-upload-dialog .el-dialog__body) {
  padding: 20px 24px 12px;
}

:deep(.project-file-upload-dialog .el-dialog__footer) {
  padding: 14px 24px 20px;
  border-top: 1px solid #edf0f5;
}

.project-file-upload-dialog-title {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #1f2937;
  font-size: 18px;
  font-weight: 600;
}

.project-file-upload-title-icon,
.project-file-upload-zone-icon,
.project-upload-file-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.project-file-upload-title-icon {
  width: 30px;
  height: 30px;
  color: #ffffff;
  background: #409eff;
  border-radius: 7px;
  font-size: 17px;
}

.project-file-upload-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.project-file-upload-zone {
  width: 100%;
}

.project-file-upload-zone :deep(.el-upload),
.project-file-upload-zone :deep(.el-upload-dragger) {
  width: 100%;
}

.project-file-upload-zone :deep(.el-upload-dragger) {
  box-sizing: border-box;
  height: 176px;
  padding: 28px 20px;
  border: 1px dashed #b9d8f9;
  border-radius: 8px;
  background: #f8fbff;
  transition: border-color 0.2s ease, background-color 0.2s ease;
}

.project-file-upload-zone :deep(.el-upload-dragger:hover) {
  border-color: #409eff;
  background: #f0f7ff;
}

.project-file-upload-zone-icon {
  width: 52px;
  height: 52px;
  margin-bottom: 10px;
  color: #409eff;
  background: #eaf4ff;
  border-radius: 50%;
  font-size: 28px;
}

.project-file-upload-zone-title {
  color: #303133;
  font-size: 15px;
  font-weight: 600;
  line-height: 22px;
}

.project-file-upload-zone-tip {
  margin-top: 4px;
  color: #909399;
  font-size: 13px;
  line-height: 20px;
}

.project-upload-file-list {
  max-height: 190px;
  overflow-y: auto;
  border: 1px solid #edf0f5;
  border-radius: 8px;
  background: #fafbfd;
}

.project-upload-file-list-header,
.project-upload-file-item,
.project-upload-file-list-actions {
  display: flex;
  align-items: center;
}

.project-upload-file-list-header {
  justify-content: space-between;
  min-height: 42px;
  padding: 0 12px 0 14px;
  color: #303133;
  font-size: 13px;
  font-weight: 600;
  border-bottom: 1px solid #edf0f5;
}

.project-upload-file-list-actions {
  gap: 8px;
  color: #909399;
  font-weight: 400;
}

.project-upload-file-list-actions :deep(.el-button) {
  padding: 5px;
}

.project-upload-file-item {
  gap: 10px;
  min-height: 42px;
  padding: 0 8px 0 14px;
  background: #ffffff;
  border-bottom: 1px solid #f0f2f5;
}

.project-upload-file-item:last-child {
  border-bottom: none;
}

.project-upload-file-icon {
  flex: 0 0 auto;
  color: #6c9eda;
  font-size: 18px;
}

.project-upload-file-name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  color: #4b5563;
  font-size: 13px;
  line-height: 20px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.project-upload-file-item :deep(.el-button) {
  flex: 0 0 auto;
  padding: 6px;
  color: #909399;
}

.project-upload-file-item :deep(.el-button:hover) {
  color: #f56c6c;
}

/* 输入控件设置最小宽度，同时保持自适应宽度 */
:deep(.el-input),
:deep(.el-textarea),
:deep(.el-date-editor),
:deep(.el-input-number),
:deep(.el-select) {
  width: 100%;
  min-width: 100px;
}

@media (max-width: 768px) {
  .limited-field {
    width: 100%;
  }

  :deep(.el-input),
  :deep(.el-textarea),
  :deep(.el-date-editor),
  :deep(.el-input-number),
  :deep(.el-select) {
    min-width: 100px;
  }
}
</style>

<style lang="less" scoped>
.form-editor-container {
  /* 页面 padding / 背景 / 滚动由 layout 统一处理 */
  height: 100%;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;

  .el-card {
    flex: 1;
    height: 0;
    max-height: 100%;
    display: flex;
    flex-direction: column;

    :deep(.el-card__body) {
      flex: 1;
      height: 0;
      padding: 0;
      overflow: hidden;
      display: flex;
      flex-direction: column;
      min-height: 0;
    }
  }

  .header {
    display: flex;
    align-items: center;
    justify-content: space-between;

    h3 {
      margin: 0;
      font-size: 18px;
      font-weight: 500;
    }
  }

  .main-content {
    display: flex;
    flex: 1;
    height: 0;
    min-height: 0;
    overflow: hidden;

    .resize-handle {
      width: 6px;
      background: #e4e7ed;
      cursor: col-resize;
      flex-shrink: 0;
      transition: background-color 0.2s;
      position: relative;

      &:hover {
        background: #409eff;
      }

      &::before {
        content: '';
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 2px;
        height: 30px;
        background: #909399;
        border-radius: 1px;
      }

      &:hover::before {
        background: white;
      }
    }

    .left-panel {
      flex-shrink: 0;
      border-right: none;
      display: flex;
      flex-direction: column;
      height: 100%;
      min-height: 0;

      .list-header {
        padding: 0.8rem 0.5rem;
        border-bottom: 1px solid #e4e7ed;
        display: flex;
        align-items: center;
        gap: 8px;
        flex-wrap: wrap;

        .quick-keyword-input {
          width: 220px;
          max-width: 100%;
        }
      }

      .form-list-wrapper {
        flex: 1;
        overflow: hidden;
        padding: 0.5rem;
        display: flex;
        flex-direction: column;

        :deep(.dynamic-table-card) {
          box-shadow: none;
          border: none;
          height: 100%;
          display: flex;
          flex-direction: column;

          .el-card__body {
            flex: 1;
            height: 0;
            overflow: hidden;
            display: flex;
            flex-direction: column;
          }
        }

        :deep(.el-table) {
          height: 100%;

          .el-table__body-wrapper {
            flex: 1;
            height: 0;
          }

          .el-table__row {
            cursor: pointer;

            &:hover {
              background-color: #f5f7fa;
            }

            &.current-row {
              background-color: #ecf5ff;

              td {
                background-color: #ecf5ff;
              }
            }
          }

          .el-table__body-wrapper {
            .el-button--danger {
              padding: 5px 10px;
              min-height: auto;

              .el-icon {
                display: inline-flex;
                align-items: center;
                justify-content: center;
              }
            }
          }
        }

        .empty-list {
          display: flex;
          align-items: center;
          justify-content: center;
          height: 200px;
          color: #909399;
          font-size: 14px;
        }

      }
    }

    // 右侧编辑区域
    .right-panel {
      flex: 1;
      width: 50%;
      display: flex;
      flex-direction: column;
      padding: 1rem;
      min-width: 0;
      min-height: 0;
      overflow: hidden;

      .edit-area {
        flex: 1;
        height: 0;
        min-height: 0;
        display: flex;
        flex-direction: column;
        min-width: 0;
        overflow-y: auto;
        overflow-x: hidden;
        background: white;
        border-radius: 8px;
        padding: 1rem;
      }

      .form-text {
        font-size: 14px;
        color: #303133;
        line-height: 32px;
      }

      .empty-state {
        flex: 1;
        height: 0;
        min-height: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        overflow: hidden;
        background: white;
        border-radius: 8px;
      }

      // 编辑模式控制区域
      .edit-mode-control {
        margin-bottom: 1rem;
        padding: 0.75rem;
        background: white;
        border-radius: 8px;
        border: 1px solid #e4e7ed;
        display: flex;
        align-items: center;
        gap: 12px;
      }

      .current-file-badge {
        margin-left: auto;

        :deep(.el-badge__content) {
          background-color: #67c23a;
          border-color: #67c23a;
        }
      }

      .form-content {
        flex: 1;
        padding: 1rem;
        overflow-y: auto;

        .building-count-item {
          .building-count-row {
            display: flex;
            align-items: center;
            gap: 12px;

            .el-input-number {
              width: 160px;
            }
          }
        }

        .buildings-container {
          margin-top: 20px;

          .buildings-header {
            h3 {
              margin: 0 0 15px 0;
              color: #303133;
            }
          }

          .buildings-list {
            margin-bottom: 20px;
          }

          .floor-setting {
            h4 {
              margin: 0 0 15px 0;
              color: #303133;
            }

            .floor-documents-section {
              display: flex;
              flex-direction: column;
              gap: 16px;

              .floor-documents-container {
                display: flex;
                gap: 16px;

                .floor-list {
                  min-width: 120px;
                  border: 1px solid #e4e7ed;
                  border-radius: 8px;
                  overflow: hidden;
                  background: #fafafa;

                  .floor-list-item {
                    padding: 10px 16px;
                    cursor: pointer;
                    transition: background 0.2s ease, color 0.2s ease;
                    border-bottom: 1px solid #e4e7ed;

                    &:last-child {
                      border-bottom: none;
                    }

                    &.active {
                      background: #409eff;
                      color: #fff;
                    }

                    &:hover:not(.active) {
                      background: #ecf5ff;
                    }
                  }
                }

                .floor-upload-panel {
                  flex: 1;
                  min-width: 0;
                  padding: 16px;
                  border: 1px solid #e4e7ed;
                  border-radius: 8px;
                  background: #fff;

                  .floor-upload-header {
                    margin-bottom: 12px;

                    h5 {
                      margin: 0;
                      font-size: 16px;
                      color: #303133;
                    }
                  }

                  .upload-progress-container {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    padding: 30px;

                    .progress-text {
                      font-size: 14px;
                      font-weight: bold;
                      color: #409eff;
                    }

                    .progress-label {
                      margin-top: 12px;
                      font-size: 14px;
                      color: #909399;
                    }
                  }

                  .floor-upload {
                    width: 100%;

                    .upload-icon {
                      font-size: 48px;
                      color: #409eff;
                      margin-bottom: 8px;
                    }
                  }

                  .uploaded-files-info {
                    margin-top: 16px;
                    padding: 12px;
                    background: #f0f9ff;
                    border: 1px solid #bfdbfe;
                    border-radius: 6px;

                    .files-header {
                      display: flex;
                      align-items: center;
                      margin-bottom: 8px;
                      font-weight: 500;
                      color: #1e40af;
                    }

                    .files-list {
                      .file-item {
                        display: flex;
                        align-items: center;
                        justify-content: space-between;
                        padding: 6px 8px;
                        margin-bottom: 4px;
                        background: white;
                        border: 1px solid #e5e7eb;
                        border-radius: 4px;

                        .file-content {
                          display: flex;
                          align-items: center;
                          flex: 1;
                          cursor: pointer;

                          .file-thumbnail-wrapper {
                            position: relative;
                            width: 40px;
                            height: 40px;
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            margin-right: 8px;
                            border: 1px solid #e4e7ed;
                            border-radius: 4px;
                            overflow: hidden;
                            background-color: #f5f7fa;

                            .file-thumbnail {
                              width: 100%;
                              height: 100%;
                              object-fit: cover;
                            }

                            .pdf-thumbnail {
                              display: flex;
                              flex-direction: column;
                              align-items: center;
                              justify-content: center;
                              width: 100%;
                              height: 100%;
                              background: linear-gradient(135deg, #fff5f5 0%, #ffe4e4 100%);

                              .pdf-label {
                                font-size: 10px;
                                color: #f56c6c;
                                font-weight: bold;
                                margin-top: 2px;
                              }
                            }

                            .el-icon {
                              font-size: 20px;
                              color: #909399;
                            }
                          }

                          .file-name {
                            flex: 1;
                            // margin-left: 8px; // removed as wrapper has margin
                            font-size: 14px;
                            color: #374151;
                          }
                        }

                        .file-remove-btn {
                          margin-left: 8px;
                          padding: 4px;
                          min-height: auto;
                        }

                        &:hover {
                          background: #f9fafb;
                        }
                      }
                    }
                  }
                }
              }

              .floor-documents-empty {
                padding: 12px;
                background: #f5f7fa;
                border-radius: 6px;
                color: #909399;
              }
            }




          }

          .action-buttons {
            margin-top: 24px;
            padding-top: 16px;
            border-top: 1px solid #e4e7ed;

            .action-buttons-row {
              display: flex;
              align-items: center;
              gap: 12px;
              margin-bottom: 20px;
            }


          }

          .action-buttons-right {
            margin-top: 10px;
            float: right;
          }

          .selected-projects-tags {
            margin-top: 15px;
            padding: 12px;
            background: #f0f9ff;
            border: 1px solid #bfdbfe;
            border-radius: 6px;

            .tags-title {
              font-weight: 500;
              color: #1e40af;
              margin-bottom: 8px;
              font-size: 14px;
            }

            .tags-container {
              display: flex;
              flex-wrap: wrap;
              gap: 8px;

              .project-tag-wrapper {
                display: inline-flex;
                max-width: 200px;

                &[draggable='true'] {
                  cursor: grab;
                }

                &.is-dragging {
                  opacity: 0.45;
                }
              }

              .project-tag {
                margin: 0;
                font-size: 13px;
                max-width: 200px;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
              }
            }
          }
        }
      }
    }
  }
}

.image-preview-container {
  text-align: center;
  min-height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;

  .preview-image {
    max-width: 100%;
    max-height: 70vh;
  }

  .preview-pdf {
    width: 100%;
    height: 70vh;
    border: none;
  }

  .preview-loading {
    font-size: 16px;
    color: #909399;
  }
}

// 项目包详情弹框样式
.package-detail-content {
  .package-info {
    h3 {
      margin: 0 0 10px 0;
      font-size: 18px;
      font-weight: 600;
      color: #303133;
    }

    .package-description {
      margin: 0;
      font-size: 14px;
      color: #606266;
      line-height: 1.6;
    }
  }

  .package-projects {
    h4 {
      margin: 0 0 15px 0;
      font-size: 16px;
      font-weight: 500;
      color: #303133;
    }
  }
}

// 项目助理和执行团队显示区域
.member-select-row {
  display: flex;
  align-items: center;
  gap: 8px;

  .form-text {
    flex: 1;
  }
}

.team-display-section {
  .team-tags-inline {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    align-items: center;
    min-height: 32px;

    .team-tag {
      margin: 0;
    }

    .dept-tag {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      font-weight: 600;
      padding: 0 10px;
      height: 28px;

      .dept-tag-icon {
        font-size: 14px;
        margin-right: 2px;
      }
    }
  }
}

// 主责部门选择弹框
.dept-dialog-content {
  display: flex;
  flex-direction: column;
  height: 420px;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  overflow: hidden;

  .dept-search-box {
    padding: 10px 12px;
    border-bottom: 1px solid #f0f0f0;
    flex-shrink: 0;
  }

  .dept-tree-wrapper {
    flex: 1;
    overflow-y: auto;
    padding: 8px 12px;

    .tree-node {
      display: flex;
      align-items: center;
      gap: 6px;

      .node-icon {
        color: #409eff;
        font-size: 16px;
      }

      .node-label {
        font-size: 13px;
      }
    }

    :deep(.el-tree-node__content) {
      height: 34px;
      border-radius: 4px;
    }

    :deep(.el-tree-node.is-current > .el-tree-node__content) {
      background-color: #ecf5ff;
      color: #409eff;
    }
  }
}

// 执行团队选择弹框
.team-dialog-content {
  display: flex;
  height: 500px;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  overflow: hidden;

  .team-left-panel {
    width: 280px;
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    border-right: 1px solid #e4e7ed;
    background: #fafafa;
  }

  .team-right-panel {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  .team-panel-header {
    padding: 10px 14px;
    border-bottom: 1px solid #e4e7ed;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-shrink: 0;

    .team-panel-title {
      font-size: 14px;
      font-weight: 600;
      color: #303133;
    }
  }

  .team-search-box {
    padding: 8px 12px;
    border-bottom: 1px solid #f0f0f0;
    flex-shrink: 0;
  }

  .team-tree-wrapper {
    flex: 1;
    overflow-y: auto;
    padding: 6px 10px;

    .tree-node {
      display: flex;
      align-items: center;
      gap: 6px;

      .node-icon {
        color: #409eff;
        font-size: 16px;
      }

      .node-label {
        font-size: 13px;
      }
    }

    :deep(.el-tree-node__content) {
      height: 32px;
      border-radius: 4px;
    }

    :deep(.el-tree-node.is-current > .el-tree-node__content) {
      background-color: #ecf5ff;
      color: #409eff;
    }
  }

  .team-tree-options {
    padding: 6px 12px;
    border-top: 1px solid #f0f0f0;
    flex-shrink: 0;
  }

  .team-user-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;
    overflow: hidden;
  }

  .team-filter-bar {
    padding: 8px 14px;
    border-bottom: 1px solid #f0f0f0;
    display: flex;
    gap: 8px;
    align-items: center;
    flex-shrink: 0;
  }

  .team-table-wrapper {
    flex: 1;
    padding: 8px 14px;
    overflow: hidden;
  }

  .team-empty-state {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .team-selected-preview {
    padding: 8px 14px;
    border-top: 1px solid #e4e7ed;
    background: #f0f9eb;
    flex-shrink: 0;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 4px;
    max-height: 80px;
    overflow-y: auto;

    .preview-label {
      font-size: 13px;
      color: #67c23a;
      font-weight: 500;
      white-space: nowrap;
    }
  }
}
</style>
